<template>
  <div
    class="user-plan-panel"
    :class="{ 'plan-sidebars-active': isAnySidebarOpen }"
  >
    <b-modal
      id="modal-exempt"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      no-close-on-backdrop
      :title="$t('Exempt Fee')"
    >
      <b-row class="">
        <b-col md="12" v-if="showExDate">
          <b-form-group
            :label="$t('Date')"
            :invalid-feedback="$t('date is required.')"
            ref="ex_date"
          >
            <flat-pickr
              ref="ex_date"
              :config="config"
              v-model="exObj.date"
              class="form-control"
              :placeholder="$t('Select Date')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('Reason')"
            :invalid-feedback="$t('Reason is required.')"
            ref="ex_reason"
          >
            <b-form-textarea
              :placeholder="$t('Enter reason')"
              rows="3"
              v-model.trim="exObj.reason"
            />
          </b-form-group>
        </b-col>

        <b-col class="text-right">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="primary"
            :disabled="exempting"
            @click="ExemptWithNote()"
          >
            <b-spinner v-if="exempting" small type="grow" />
            <span v-else class="align-middle">{{ $t("Exempt") }}</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      id="modal-remarks"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      no-close-on-backdrop
      :title="$t('Remarks')"
      @hidden="$refs.attendanceTable.clearSelected()"
    >
      <b-row class="" v-if="attObj">
        <b-col md="12">
          <p>
            Enter Remarks for
            <strong>
              {{
                new Date(attObj.date).toLocaleDateString("en-UK", {
                  year: "numeric",
                  day: "numeric",
                  month: "short",
                })
              }}
            </strong>
            having status
            <b-badge :variant="getVariant(attObj.status)">
              {{ attObj.status }}
            </b-badge>
          </p>
        </b-col>

        <b-col md="12">
          <b-form-group
            :invalid-feedback="$t('Remarks is required.')"
            ref="att_remarks"
          >
            <b-form-textarea
              :placeholder="$t('Enter remarks')"
              rows="3"
              v-model.trim="attObj.meta_notes"
            />
          </b-form-group>
        </b-col>

        <b-col class="text-right">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="success"
            :disabled="attSaving"
            @click="editAtt()"
          >
            <b-spinner v-if="attSaving" small type="grow" />
            <span v-else class="align-middle">{{ $t("Save") }}</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Custom fee dates — only when setting custom_fees_date is enabled -->
    <b-modal
      v-if="isCustomFeesDateEnabled"
      id="modal-custom-fees-date"
      :title="$t('Apply custom fee dates')"
      centered
      @hidden="resetCustomFeesDateForm"
    >
      <div class="custom-fees-modal-summary mb-2">
        <div v-if="loadingCustomFeesDateModal" class="text-center py-1">
          <b-spinner small type="grow" variant="primary" />
          <span class="ml-50 text-muted">{{ $t("Loading…") }}</span>
        </div>
        <template v-else>
          <div
            class="d-flex flex-nowrap align-items-center custom-fees-modal-badge-row overflow-auto pb-50"
          >
            <template v-if="customFeesDateHasSavedRecord">
              <span class="flex-shrink-0 small mr-75 align-middle">
                <span class="font-weight-semibold text-dark">{{
                  $t("Saved")
                }}</span
                ><span class="font-weight-semibold text-dark">{{
                  $t(" dates of")
                }}</span>
              </span>
            </template>
            <template v-if="customFeesDateSelectedDescriptors.length">
              <b-badge
                v-for="(desc, idx) in customFeesDateSelectedDescriptors"
                :key="'cfd-' + (desc.fid != null ? desc.fid : idx)"
                variant="light-primary"
                pill
                class="flex-shrink-0 mr-50 font-weight-medium"
              >
                {{ desc.badgeLabel }}
              </b-badge>
            </template>
            <span v-else class="text-muted small mb-0 flex-shrink-0">
              {{ $t("Selected fees") }}
            </span>
          </div>
        </template>
      </div>
      <b-row>
        <b-col md="12" class="mb-1">
          <b-form-group :label="$t('Issue Date')">
            <flat-pickr
              :config="config"
              v-model="customFeesDateForm.issueDate"
              class="form-control"
              :placeholder="$t('Select issue date')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12" class="mb-1">
          <b-form-group :label="$t('Due Date')">
            <flat-pickr
              :config="config"
              v-model="customFeesDateForm.dueDate"
              class="form-control"
              :placeholder="$t('Select due date')"
            />
          </b-form-group>
        </b-col>
        <b-col md="12" class="mb-1">
          <b-form-group :label="$t('Expiry Date')">
            <flat-pickr
              :config="config"
              v-model="customFeesDateForm.expiryDate"
              class="form-control"
              :placeholder="$t('Select expiry date')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button
          v-if="customFeesDateLoadedRecord && customFeesDateLoadedRecord.id"
          variant="outline-danger"
          :disabled="removingCustomFeesDate || savingCustomFeesDate"
          @click="removeCustomFeesDate()"
        >
          <b-spinner v-if="removingCustomFeesDate" small class="mr-50" />
          {{ $t("Remove") }}
        </b-button>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('modal-custom-fees-date')"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="
            savingCustomFeesDate ||
            removingCustomFeesDate ||
            loadingCustomFeesDateModal
          "
          @click="saveCustomFeesDate()"
        >
          <b-spinner v-if="savingCustomFeesDate" small class="mr-50" />
          {{ $t("Save") }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-att-report"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      no-close-on-backdrop
      :title="$t('Attendance Report')"
    >
      <b-row class="">
        <b-col md="12">
          <b-form-group
            :label="$t('Date')"
            :invalid-feedback="$t('date is required.')"
            ref="rep_date"
          >
            <flat-pickr
              ref="rep_date"
              :config="config2"
              v-model="reportObj.rangeDate"
              class="form-control"
              :placeholder="$t('Select Date')"
              @on-change="setRepDate"
            />
          </b-form-group>
        </b-col>

        <b-col class="mt-1" md="12">
          <b-button variant="primary" @click="getReport()">
            <span>{{ $t("Show") }}</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <portal to="user-plan-sidebars">
      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header plan-sidebar-header"
        >
          <h5 class="mb-0">{{ $t(sidebarTitle) }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="18"
            @click="visibility = false"
          />
        </div>
        <b-form class="plan-sidebar-form" @submit.prevent>
          <b-row>
            <b-col md="12">
              <b-form-group
                :label="$t('Complaint')"
                :invalid-feedback="$t('complaint is required.')"
                ref="complaint"
              >
                <b-form-input
                  id="mc-first-name"
                  :placeholder="$t('Enter complaint')"
                  name="classField"
                  ref="complaint"
                  v-model="compObj.complaint"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Date')"
                :invalid-feedback="$t('date is required.')"
                ref="compDate"
              >
                <flat-pickr
                  ref="compDate"
                  :config="config"
                  v-model="compObj.date"
                  class="form-control"
                  :placeholder="$t('Select Date')"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Upload Documents')"
                :invalid-feedback="$t('Documents are required')"
              >
                <b-button
                  ref="docs"
                  @click="$refs.attachment.click()"
                  variant="primary"
                  class="plan-sidebar-btn"
                  :disabled="request || mydocloading"
                  block
                >
                  <b-spinner
                    v-if="mydocloading"
                    type="grow"
                    small
                    :label="$t('Loading...')"
                  />
                  <span v-else>{{ $t("Upload") }}</span>
                </b-button>
                <input
                  type="file"
                  id="attachment"
                  hidden
                  ref="attachment"
                  @change="uploadAtt()"
                />
              </b-form-group>
            </b-col>
            <b-col
              md="12"
              v-if="mediaLoaded"
              style="display: flex; align-items: center"
            >
              <div class="file-type">
                {{ compObj.attachment.split(".").pop().toUpperCase() }}
              </div>
              <feather-icon
                icon="Trash2Icon"
                size="22"
                class="text-danger cursor-pointer"
                @click="removeAtt()"
              />
            </b-col>

            <b-col md="12" class="mt-1">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                class="plan-sidebar-btn"
                @click="saveComp()"
                :disabled="request"
                block
              >
                <b-spinner v-if="request" small type="grow" />
                <span v-else> {{ $t(buttonTitle) }} </span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility2"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Resolve Complaint") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility2 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row>
            <b-col md="12">
              <b-form-group
                :label="$t('Note')"
                :invalid-feedback="$t('Note is required.')"
                ref="note"
              >
                <b-form-input
                  id="mc-first-name"
                  :placeholder="$t('Enter note')"
                  name="classField"
                  ref="note"
                  v-model="resObj.note"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Date')"
                :invalid-feedback="$t('date is required.')"
                ref="resdate"
              >
                <flat-pickr
                  ref="resdate"
                  :config="config"
                  v-model="resObj.resolvedOn"
                  class="form-control"
                  :placeholder="$t('Select Date')"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Resolved By')"
                :invalid-feedback="$t('resolved by is required.')"
                ref="resBy"
              >
                <b-form-input
                  id="mc-first-name"
                  :placeholder="$t('Enter name')"
                  name="classField"
                  ref="resBy"
                  v-model="resObj.resolvedBy"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                class="mr-1"
                @click="save()"
                :disabled="request"
                block
              >
                <b-spinner v-if="request" small type="grow" />
                <span v-else> {{ $t("Resolve") }} </span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility3"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Initiate Admission Fee") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility3 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row>
            <b-col md="12">
              <div class="mb-50">
                <b-badge variant="primary" class="font-small-3">
                  {{ $t("Current Campus") }}: {{ getCampus(userData.campusID) }}
                </b-badge>
              </div>
              <div class="mb-50">
                <b-badge variant="primary" class="font-small-3">
                  {{ $t("Admitted Campus") }}:
                  {{ getCampus(userData.campus_Admitted) }}
                </b-badge>
              </div>
            </b-col>
            <!-- <b-col md="12">
            <b-form-group
              label="Select Month"
              invalid-feedback="month is required."
              ref="month"
            >
              <v-select
                v-model="feeObj.months"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="months"
                :reduce="(op) => op.value"
                :clearable="false"
                label="text"
                placeholder="Select month"
              />
            </b-form-group>
          </b-col> -->
            <b-col md="12">
              <b-form-group
                :label="$t('Select Months')"
                :invalid-feedback="$t('month is required.')"
                ref="month"
              >
                <!-- style="margin: 7px" -->
                <b-button
                  class="btn-icon mr-50 mb-50"
                  v-for="(mon, ind) in months"
                  :key="ind"
                  :variant="
                    feeObj.months.includes(mon.value)
                      ? 'primary'
                      : 'outline-primary'
                  "
                  @click="selectMonth(mon.value)"
                >
                  {{ mon.value }}
                </b-button>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Select Year')"
                :invalid-feedback="$t('year is required.')"
                ref="year"
              >
                <v-select
                  v-model="feeObj.year"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="years"
                  :clearable="false"
                  label="name"
                  :placeholder="$t('Select year')"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                @click="saveFee()"
                :disabled="saving"
                block
              >
                <b-spinner v-if="saving" small type="grow" />
                <span v-else> {{ $t("Create") }} </span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility4"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Receive Fee") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility4 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row>
            <b-col md="12">
              <div class="mb-50">
                <b-badge variant="primary" class="font-small-3">
                  {{ $t("Current Campus") }}: {{ getCampus(userData.campusID) }}
                </b-badge>
              </div>
              <div class="mb-50">
                <b-badge variant="primary" class="font-small-3">
                  {{ $t("Admitted Campus") }}:
                  {{ getCampus(userData.campus_Admitted) }}
                </b-badge>
              </div>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Collection Account')"
                :invalid-feedback="$t('Collection account is required.')"
                ref="acc"
              >
                <v-select
                  v-model="recObj.accountID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="accTypes"
                  :reduce="(opt) => opt.id"
                  label="account"
                  :clearable="false"
                  :placeholder="$t('Select account type')"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('Receiving Date')"
                :invalid-feedback="$t('Receiving date is required.')"
                ref="rcvDate"
              >
                <flat-pickr
                  :config="config"
                  v-model="recObj.rcvDate"
                  class="form-control"
                  :disabled="!showExDate"
                  :placeholder="$t('Select Date')"
                />
              </b-form-group>
            </b-col>
            <template v-if="chDetails && chDetails.valueBool">
              <b-col md="12">
                <b-form-group
                  :invalid-feedback="$t('Organization is required.')"
                >
                  <div class="d-flex justify-content-between">
                    <label class="bv-no-focus-ring col-form-label pt-0">{{
                      $t("Organization")
                    }}</label>
                    <feather-icon
                      class="cursor-pointer"
                      icon="EditIcon"
                      size="20"
                      v-b-tooltip.hover.top
                      :title="$t('Organization')"
                      @click="openOrg()"
                    />
                  </div>
                  <v-select
                    v-model="otherObj.organization"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="[{ id: 0, title: 'None' }, ...orgList]"
                    :reduce="(opt) => opt.id"
                    label="title"
                    :clearable="false"
                    :placeholder="$t('Select organization')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group
                  :label="$t('Bank Name')"
                  :invalid-feedback="$t('Bank Name is required.')"
                >
                  <b-form-input
                    v-model="otherObj.bank"
                    :placeholder="$t('Enter bank name')"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group
                  :label="$t('Cheque No.')"
                  :invalid-feedback="$t('Cheque No is required.')"
                >
                  <b-form-input
                    v-model="otherObj.cheque"
                    :placeholder="$t('Enter cheque number')"
                  />
                </b-form-group>
              </b-col>
            </template>

            <b-col md="12">
              <b-form-group
                :label="$t('Receipt No.')"
                :invalid-feedback="$t('Receipt No is required.')"
                ref="rcptNo"
              >
                <b-form-input
                  type="number"
                  v-model="recObj.barcode"
                  :placeholder="$t('Enter receipt number')"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                :label="$t('Note')"
                :invalid-feedback="$t('Note is required.')"
                ref="rec_note"
              >
                <b-form-textarea
                  :placeholder="$t('Enter note')"
                  rows="3"
                  v-model.trim="recObj.receiveNote"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <h5>{{ $t("Receiving Amount") }}: {{ totalAmount }}</h5>
            </b-col>

            <b-col md="12" class="mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                @click="receiveFee()"
                :disabled="receiving"
                block
              >
                <b-spinner v-if="receiving" small type="grow" />
                <span v-else> {{ $t("Receive") }} </span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility5"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Installments") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility5 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row class="">
            <b-col md="12">
              <b-form-group :label="$t('Current Balance')" ref="currBal">
                <b-form-input
                  disabled
                  placeholder=""
                  ref="currBal"
                  v-model="instCurrent"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                :label="$t('Installment Amount')"
                :invalid-feedback="$t('Invalid amount entered')"
                ref="instAmount"
              >
                <b-form-input
                  type="number"
                  :placeholder="$t('Enter amount')"
                  v-model="instObj.amount"
                  @input="checkInstallment()"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="success"
                class="mt-1"
                @click="saveInstallment()"
                :disabled="instSaving"
                block
              >
                <b-spinner v-if="instSaving" small type="grow" />
                <span v-else>{{ $t("Create Installment") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility6"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Discount") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility6 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row class="">
            <b-col md="12">
              <b-form-group :label="$t('Current Balance')" ref="currBal">
                <b-form-input
                  disabled
                  placeholder=""
                  v-model="currentBalance"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                :label="$t('Discount')"
                :invalid-feedback="$t('Invalid amount entered')"
                ref="discount"
              >
                <b-form-input
                  type="number"
                  :placeholder="$t('Enter discount amount')"
                  v-model="disObj.discountAmount"
                  @input="validateDiscount()"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group :label="$t('Net Balance')" ref="netBal">
                <b-form-input disabled placeholder="" v-model="netBalance" />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                :label="$t('Note')"
                :invalid-feedback="$t('Note is required.')"
                ref="dis_note"
              >
                <b-form-textarea
                  placeholder="Enter note"
                  rows="3"
                  v-model="disObj.discountNote"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="success"
                class="mt-1"
                @click="AddDiscount()"
                :disabled="savingDis"
                block
              >
                <b-spinner v-if="savingDis" small type="grow" />
                <span v-else>{{ $t("Save") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-Fee"
        bg-variant="white"
        v-model="visibility7"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Manage Organizations") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility7 = false"
          />
        </div>

        <b-form class="p-2" @submit.prevent>
          <b-row class="d-flex align-items-center">
            <b-col md="12">
              <b-form-group
                :label="$t('Organization Name')"
                :invalid-feedback="$t('Organization name is required.')"
              >
                <b-form-input
                  ref="org_name"
                  v-model.trim="orgObj.title"
                  :placeholder="$t('Enter organization name name')"
                  @focusout="checkOrg()"
                >
                </b-form-input>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                variant="success"
                class="btn-icon mt-1"
                @click="saveOrg()"
                :disabled="savingOrg"
                block
              >
                <b-spinner v-if="savingOrg" small type="grow" />
                <span v-else>{{ $t("Save") }}</span>
              </b-button>
            </b-col>
          </b-row>
          <b-table
            class="mt-2"
            show-empty
            :items="orgList"
            :fields="orgFields"
            responsive
            hover
          >
            <template #head()="scope">
              <div>
                <span>{{ $t(scope.label) }}</span>
              </div>
            </template>

            <template #head(actions)="data">
              <div class="text-right">
                <span>{{ $t(data.label) }}</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="text-right">
                <b-button
                  variant="primary"
                  class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                  @click="editOrg(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <!-- <b-button
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteOrg(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button> -->
              </div>
            </template>
          </b-table>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="visibility8"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Transfer Student") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="visibility8 = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row class="">
            <b-col md="12" class="mb-1">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="44"
                    :src="getPicturePreview(chObj.picture)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                </template>

                <b-link
                  class="font-weight-bold d-block text-nowrap"
                  style="margin-bottom: 4px"
                >
                  <b-badge variant="light-primary">
                    {{ chObj.name }}
                  </b-badge>
                </b-link>
                <small class="text-muted">
                  <b-badge variant="light-primary" class="mr-50">
                    {{ $t("GR") }} -
                    {{
                      reportDomain === "myskoolhpgs" ||
                      reportDomain === "myskoolprofectus"
                        ? chObj.grNoLocal
                        : chObj.grNo
                    }}
                  </b-badge>
                  <b-badge variant="light-primary" class="mr-50">
                    {{ $t("F#") }} - {{ chObj.family_code }}
                  </b-badge>
                  <b-badge variant="light-primary">
                    {{
                      reportDomain === "myskoolhpgs" ||
                      reportDomain === "myskoolprofectus"
                        ? $t("CID") + ": "
                        : $t("ID") + ": "
                    }}

                    {{
                      reportDomain === "myskoolhpgs" ||
                      reportDomain === "myskoolprofectus"
                        ? chObj.grNo
                        : chObj.id
                    }}
                  </b-badge>
                </small>
              </b-media>
            </b-col>
            <b-col cols="12">
              <b-form-group :label="$t('Current Campus')">
                <b-badge variant="primary">
                  {{ getCampus(chObj.campusID) }}
                </b-badge>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                :label="$t('Transfer to Campus')"
                :invalid-feedback="$t('Campus is required.')"
                ref="ch_camp"
              >
                <v-select
                  v-model="chObj.newCampus"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="campuses"
                  :clearable="false"
                  label="branch"
                  :reduce="(val) => val.id"
                  :disabled="clsLoading"
                  :placeholder="$t('Select campus')"
                  @input="LoadClass()"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                :label="$t('Class')"
                :invalid-feedback="$t('Class is required')"
                ref="ch_cls"
              >
                <v-select
                  v-model="chObj.clsID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="chOptions"
                  :clearable="false"
                  label="name"
                  :reduce="(val) => val.id"
                  :disabled="clsLoading"
                  :placeholder="$t('Select class')"
                  @input="FillChSec()"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group
                :label="$t('Section')"
                :invalid-feedback="$t('Section is required')"
                ref="ch_sec"
              >
                <v-select
                  v-model="chObj.secID"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="chSections"
                  :reduce="(opt) => opt.id"
                  label="section"
                  :clearable="false"
                  :disabled="clsLoading"
                  :placeholder="$t('Select section')"
                  @input="checkChSec()"
                />
              </b-form-group>
            </b-col>

            <b-col class="mt-1" md="12">
              <b-button
                type="submit"
                variant="success"
                block
                :disabled="transfering"
                @click="saveCh()"
              >
                <b-spinner v-if="transfering" small type="grow" />
                <span v-else>{{ $t("Transfer") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>

      <b-sidebar
        id="sidebar-right"
        ref="mySidebar"
        bg-variant="white"
        v-model="showHold"
        sidebar-class="sidebar-lg plan-form-sidebar"
        right
        no-header
        backdrop
        shadow
        :z-index="12000"
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h5 class="mb-0">{{ $t("Hold Fees") }}</h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="showHold = false"
          />
        </div>
        <b-form class="p-2" @submit.prevent>
          <b-row class="mt-1">
            <b-col md="12">
              <b-form-group
                :label="$t('Date')"
                :invalid-feedback="$t('Date is required')"
                ref="hold_date"
              >
                <flat-pickr
                  :config="config"
                  v-model="holdObj.date"
                  class="form-control"
                  :placeholder="$t('Select Date')"
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-form-group
                :label="$t('Reason')"
                :invalid-feedback="$t('Reason')"
                ref="reason"
              >
                <b-form-textarea
                  :placeholder="$t('Write reason for holding')"
                  v-model.trim="holdObj.reason"
                  rows="4"
                ></b-form-textarea>
              </b-form-group>
            </b-col>

            <b-col md="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                @click="saveHold()"
                :disabled="holdSaving"
                block
              >
                <b-spinner v-if="holdSaving" small type="grow" />
                <span v-else> {{ $t("Save") }} </span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-sidebar>
    </portal>

    <b-tabs
      class="user-plan-tabs pt-1"
      nav-class="custom-nav-tabs"
      content-class="user-plan-tab-panes"
      active-nav-item-class="custom-nav-active"
      no-fade
    >
      <b-tab
        :title="$t('Fee')"
        active
        v-if="reportDomain !== 'myskoolhappinessfoundation'"
      >
        <template #title>
          <feather-icon icon="BookmarkIcon" size="18" />
          <span class="tab-label">{{ $t("Fee") }}</span>
        </template>
        <div class="tab-content-wrapper animation-fade">
          <div class="fee-dues-section">
            <div
              class="tab-toolbar d-flex flex-wrap align-items-center justify-content-between"
            >
              <div>
                <template
                  v-if="rights && rights.edit && holdSett && holdSett.valueBool"
                >
                  <b-button
                    v-if="selectedID.length > 0"
                    variant="primary"
                    class="btn-icon mr-50 mb-50"
                    @click="openHold()"
                    :disabled="unholding || holdSaving"
                  >
                    {{ $t("Hold") }}
                  </b-button>
                  <b-button
                    v-if="selectedID.length > 0"
                    variant="primary"
                    class="btn-icon mr-50 mb-50"
                    @click="unHoldFee()"
                    :disabled="unholding || holdSaving"
                  >
                    {{ $t("UnHold") }}
                  </b-button>
                </template>
                <b-button
                  v-if="
                    isCustomFeesDateEnabled &&
                    userData &&
                    userData.id > 0 &&
                    selectedID.length > 0
                  "
                  variant="outline-primary"
                  class="btn-icon mr-50 mb-50"
                  @click="openCustomFeesDateModal()"
                  :disabled="
                    savingCustomFeesDate ||
                    gridLoading ||
                    loadingCustomFeesDateLoad ||
                    removingCustomFeesDate
                  "
                >
                  {{ $t("Apply custom fee dates") }}
                </b-button>
              </div>
              <div
                class="fee-toolbar-actions d-flex flex-wrap justify-content-sm-end"
              >
                <b-button
                  v-if="
                    reportDomain === 'myskoolhpgs' &&
                    (userData.status.toLowerCase() == 'registered' ||
                      (userData.status.toLowerCase() == 'present' &&
                        rights &&
                        rights.editCampus))
                  "
                  variant="outline-primary"
                  class="btn-icon mr-50 mb-50"
                  @click="openCh()"
                  >{{ $t("Transfer Campus") }}
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="btn-icon mr-50 mb-50 print-challan-btn"
                  @click="print()"
                  :disabled="printing"
                >
                  <span>{{ $t("Print Challan") }}</span>
                  <b-spinner v-if="printing" small class="ml-50" />
                </b-button>
                <b-button
                  v-if="
                    (reportDomain === 'myskoolhpgs' ||
                      reportDomain === 'myskooliuss') &&
                    userData.status.toLowerCase() == 'registered'
                  "
                  variant="success"
                  class="btn-icon mb-50"
                  @click="openInit()"
                  :disabled="
                    (reportDomain === 'myskoolhpgs' &&
                      (!admObj || !admObj.valueBool || !admObj.value)) ||
                    (reportDomain === 'myskooliuss' &&
                      admObj &&
                      !admObj.valueBool) ||
                    dues.find((el) =>
                      el.feesType.feeType.toLowerCase().includes('registration')
                    )
                  "
                >
                  {{ $t("Initiate Admission Fee") }}
                </b-button>
              </div>
            </div>

            <div class="modern-table fee-data-table">
              <b-table
                ref="dueTable"
                class="mb-0 fee-dues-table"
                show-empty
                :items="dues"
                :fields="feesFields"
                responsive
                hover
                :busy="gridLoading"
                selectable
                :select-mode="'multi'"
                @row-selected="onRowSelect($event)"
              >
                <template #empty>
                  <div class="table-empty-state">
                    <feather-icon icon="InboxIcon" size="28" class="mb-50" />
                    <span>{{ $t("There are no records to show") }}</span>
                  </div>
                </template>
                <template #table-busy>
                  <div class="d-flex justify-content-center my-2">
                    <b-spinner
                      style="width: 3rem; height: 3rem"
                      type="grow"
                      variant="primary"
                      :label="$t('Spinning')"
                    ></b-spinner>
                  </div>
                </template>
                <template #head()="scope">
                  <div>
                    <span>{{ $t(scope.label) }}</span>
                  </div>
                </template>
                <template #head(feeType)="data">
                  <div>
                    <b-form-checkbox
                      inline
                      :checked="
                        selectedID.length > 0 &&
                        selectedID.length == dues.length
                      "
                      @change="(e) => setSelectAll(e)"
                    >
                      <span class="font-small-2">{{ $t(data.label) }}</span>
                    </b-form-checkbox>
                  </div>
                </template>

                <template #cell(feeType)="data">
                  <b-form-checkbox
                    inline
                    :checked="selectedID.includes(data.item.fees.id)"
                    @change="(e) => setSelected(e, data.index)"
                  >
                    {{ data.item.feesType.feeType }}
                  </b-form-checkbox>
                </template>
                <template #cell(monthYear)="data">
                  <span class="fee-period-badge">
                    {{ data.item.fees.monthYear }}
                  </span>
                  <b-badge
                    variant="light-danger"
                    class="fee-hold-badge mt-50"
                    v-if="data.item.fees.status == 'hold'"
                  >
                    {{ $t("On Hold") }}
                  </b-badge>
                </template>
                <template #cell(lateFee)="data">
                  <b-badge variant="light-primary">
                    {{ data.item.fees.lateFee }}
                  </b-badge>
                </template>
                <template #cell(timelyDiscount)="data">
                  <b-badge variant="light-primary">
                    {{ data.item.fees.timelyDiscount }}
                  </b-badge>
                </template>
                <template #cell(balance)="data">
                  <span class="fee-balance-amount">
                    {{ data.item.fees.balance }}
                  </span>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-primary"
                    class="btn-icon mr-50"
                    v-b-tooltip.hover.bottomleft
                    :title="$t('Discount')"
                    @click="openDiscount(data.item)"
                  >
                    <feather-icon icon="PercentIcon" />
                  </b-button>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-primary"
                    class="btn-icon mr-50"
                    v-b-tooltip.hover.bottomleft
                    :title="$t('Installment')"
                    @click="openInst(data.item)"
                  >
                    <feather-icon icon="ShuffleIcon" />
                  </b-button>
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-primary"
                    class="btn-icon"
                    v-b-tooltip.hover.bottomleft
                    :title="$t('Fee Exempt')"
                    @click="openExempt(data.item.fees.id)"
                  >
                    <!-- @click="Exempt(data.item.fees.id)" -->
                    <feather-icon icon="StarIcon" />
                  </b-button>
                </template>
              </b-table>
            </div>

            <div
              class="dues-summary-bar d-flex justify-content-between align-items-center flex-wrap"
            >
              <div class="dues-summary-bar__label">
                <feather-icon icon="CreditCardIcon" size="16" class="mr-50" />
                {{ $t("Total Dues") }}
              </div>
              <div class="dues-summary-bar__amount">{{ duesTotal }}</div>
              <b-button
                v-if="
                  reportDomain === 'myskoolhpgs' &&
                  (['registered', 'enrolled'].includes(
                    userData.status.toLowerCase()
                  ) ||
                    (userData.status.toLowerCase() == 'present' &&
                      showReceive)) &&
                  dues.length > 0
                "
                variant="success"
                class="btn-icon ml-auto mt-50 mt-sm-0"
                @click="openRec()"
              >
                {{ $t("Receive Fee") }}
              </b-button>
            </div>
          </div>

          <div class="fee-history-section">
            <div class="fee-history-section__head">
              <div class="fee-history-section__title">
                <feather-icon icon="ClockIcon" size="18" class="mr-50" />
                {{ $t("Payment History") }}
              </div>
              <b-badge v-if="history.length" variant="light-primary" pill>
                {{ history.length }} {{ $t("records") }}
              </b-badge>
            </div>
            <user-view-user-timeline-card
              :history="history"
              :duesTotal="duesTotal"
            />
          </div>
        </div>
      </b-tab>
      <b-tab
        v-if="isSkhDomain"
        :title="$t('Subject')"
        lazy
        @click="loadSubjectFees"
      >
        <template #title>
          <feather-icon icon="BookOpenIcon" size="18" />
          <span class="tab-label">{{ $t("Subject") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body">
          <div class="tab-data-table fee-data-table">
            <b-table
              responsive
              :items="subjectFeeRows"
              :fields="subjectFeeFields"
              show-empty
              class="mb-0 tab-grid-table subject-view-table"
              sticky-header="420px"
              :busy="subjectFeesLoading"
              :empty-text="$t('There are no records to show')"
            >
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>
              <template #table-busy>
                <div class="d-flex justify-content-center my-2">
                  <b-spinner
                    style="width: 3rem; height: 3rem"
                    type="grow"
                    variant="primary"
                    :label="$t('Spinning')"
                  />
                </div>
              </template>
              <template #empty>
                <div class="table-empty-state">
                  <feather-icon icon="InboxIcon" size="28" class="mb-50" />
                  <span>{{ $t("There are no records to show") }}</span>
                </div>
              </template>
              <template #cell(subjectName)="data">
                <span class="subject-view-text">{{ data.value }}</span>
              </template>
              <template #cell(teacherName)="data">
                <span class="subject-view-text">{{ data.value }}</span>
              </template>
              <template #cell(subjectFee)="data">
                <span class="subject-view-amount">{{ data.value }}</span>
              </template>
              <template #cell(teacherShareAmount)="data">
                <span class="subject-view-share">{{ data.value }}</span>
              </template>
            </b-table>
          </div>
          <div
            v-if="subjectFeeRows.length"
            class="dues-summary-bar d-flex justify-content-between align-items-center flex-wrap mt-1"
          >
            <div class="dues-summary-bar__label">
              <feather-icon icon="BookOpenIcon" size="16" class="mr-50" />
              {{ $t("Total Amount") }}
            </div>
            <div class="dues-summary-bar__amount">{{ subjectTotalFee }}</div>
          </div>
        </b-card>
      </b-tab>
      <b-tab :title="$t('Attendance')" lazy>
        <template #title>
          <feather-icon icon="CheckCircleIcon" size="18" />
          <span class="tab-label">{{ $t("Attendance") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body">
          <div
            class="tab-toolbar d-flex flex-wrap align-items-center justify-content-between"
          >
            <div class="d-flex flex-wrap align-items-center filter-btn-group">
              <b-button
                variant="outline-primary"
                size="sm"
                class="filter-period-btn"
                @click="loadAttendance('week')"
              >
                {{ $t("This Week") }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                class="filter-period-btn"
                @click="loadAttendance('month')"
              >
                {{ $t("This Month") }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                class="filter-period-btn"
                @click="loadAttendance('year')"
              >
                {{ $t("This Year") }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                class="filter-period-btn"
                @click="loadAttendance('last year')"
              >
                {{ $t("Last Year") }}
              </b-button>
              <b-button
                v-if="
                  [
                    'myskoolksa',
                    'myskooliota',
                    'myskooliota2',
                    'myskoolascend',
                  ].includes(reportDomain)
                "
                variant="primary"
                @click="openReport()"
                class="ml-1"
              >
                {{ $t("Report") }}
              </b-button>
            </div>
            <b-button
              variant="primary"
              class="mt-1 mt-sm-0 attendance-export-btn"
              :disabled="dataLoading || !attendance.length"
              @click="exportAttendanceCsv"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
              {{ $t("Export") }}
            </b-button>
          </div>

          <div class="tab-data-table fee-data-table mt-1">
            <b-table
              ref="attendanceTable"
              responsive
              :items="attendance"
              :fields="attFields"
              class="mb-0 tab-grid-table"
              sticky-header="420px"
              show-empty
              :busy="dataLoading"
              hover
              :selectable="remarksVisible"
              select-mode="single"
              @row-selected="onAttSelect($event)"
            >
              <template #empty>
                <div class="table-empty-state">
                  <feather-icon icon="CalendarIcon" size="28" class="mb-50" />
                  <span>{{ $t("There are no records to show") }}</span>
                </div>
              </template>
              <template #table-busy>
                <div class="d-flex justify-content-center mb-3 mt-3">
                  <b-spinner
                    style="width: 3rem; height: 3rem"
                    variant="primary"
                    label="Spinning"
                  ></b-spinner>
                </div>
              </template>
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>
              <template #cell(status)="data">
                <span
                  class="att-status-pill"
                  :class="getAttStatusClass(data.item.status)"
                >
                  {{ data.item.status }}
                </span>
              </template>
              <template #cell(date)="data">
                <span class="table-date-chip">
                  {{ formatTableDate(data.value) }}
                </span>
              </template>
              <template #cell(timeInOut)="data">
                <span
                  v-if="!['A', 'H'].includes(data.item.status)"
                  class="table-time-text"
                >
                  <span v-if="data.item.timeIn || data.item.timeOut">
                    {{ formatTime(data.item.timeIn) }} –
                    {{ formatTime(data.item.timeOut) }}
                  </span>
                  <span v-else>—</span>
                </span>
                <span v-else class="table-time-text text-muted">—</span>
              </template>

              <template #cell(remarks)="data">
                {{ data.item.meta_notes }}
              </template>
            </b-table>
          </div>
        </b-card>
      </b-tab>
      <b-tab :title="$t('Complaints')" lazy>
        <template #title>
          <feather-icon icon="InfoIcon" size="18" />
          <span class="tab-label">{{ $t("Complaints") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body">
          <div class="complaints-toolbar">
            <b-button
              v-if="!byparent"
              variant="primary"
              class="complaints-add-btn"
              @click="openComp()"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Complaints") }}</span>
            </b-button>
            <div class="complaints-filter-group">
              <b-button
                :variant="byparent ? 'outline-primary' : 'primary'"
                class="complaints-filter-btn"
                @click="loadComplaints()"
              >
                {{ $t("By School") }}
              </b-button>
              <b-button
                :variant="byparent ? 'primary' : 'outline-primary'"
                class="complaints-filter-btn"
                @click="loadParent()"
              >
                {{ $t("By Parents") }}
              </b-button>
            </div>
          </div>

          <div class="tab-data-table fee-data-table mt-1">
            <b-table
              responsive
              :items="complaints"
              :fields="byparent ? parentFields : compFields"
              show-empty
              :busy="dataLoading"
              class="mb-0 tab-grid-table"
              sticky-header="420px"
            >
              <template #table-busy>
                <div class="d-flex justify-content-center mb-3 mt-3">
                  <b-spinner
                    style="width: 3rem; height: 3rem"
                    variant="primary"
                    label="Spinning"
                  ></b-spinner>
                </div>
              </template>
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>

              <template #cell(date)="data">
                <span class="table-date-chip">
                  {{ formatTableDate(data.item.us.date) }}
                </span>
              </template>
              <template #cell(name)="data">
                {{
                  byparent
                    ? data.item.us.about
                    : !data.item.ts
                    ? "Admin"
                    : data.item.ts.name
                }}
              </template>
              <template #cell(complaint)="data">
                <span class="table-text-cell">{{
                  byparent ? data.item.us.details : data.item.us.complaint
                }}</span>
              </template>

              <template #cell(parActions)="data">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                  @click="resolve(data.item.us)"
                >
                  <feather-icon icon="CheckIcon" />
                </b-button>
              </template>
              <template #cell(actions)="data">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                  @click="EditComp(data.item.us)"
                >
                  <feather-icon icon="EditIcon" class="" />
                </b-button>

                <b-button
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  variant="outline-danger"
                  class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                  @click="DeleteComp(data.item.us.id)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-card>
      </b-tab>
      <b-tab :title="$t('Assessment')" lazy>
        <template #title>
          <feather-icon icon="PenToolIcon" size="18" />
          <span class="tab-label">{{ $t("Assessment") }}</span>
        </template>
        <!-- <b-card-text> Graph Subject wise Subject Filter </b-card-text> -->
        <b-card class="content-panel tab-panel-body">
          <template v-if="false">
            <b-button
              variant="outline-primary"
              class="mr-1"
              v-for="ex in examTypes"
              :key="ex.id"
            >
              {{ ex.examType }}
            </b-button>
          </template>

          <div class="tab-data-table fee-data-table">
            <b-table
              v-if="!marksGrid && !showMarksDetailsPanel"
              responsive
              :items="assessments"
              :fields="resultFields"
              show-empty
              class="mb-0 tab-grid-table"
              sticky-header="420px"
            >
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>
              <template #cell(examType)="data">
                <b-badge variant="light-primary" class="m-50">
                  {{ data.value }}
                </b-badge>
              </template>
              <template #cell(session)="data">
                <b-badge variant="light-primary">
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(marks)="data">
                <b-badge variant="light-primary">
                  {{ data.item.results.obtainedMarks }} /
                  {{ data.item.results.totalMarks }}
                </b-badge>
              </template>

              <template #cell(percentage)="data">
                <b-badge variant="light-primary">
                  {{ data.item.results.percentage }} <span>%</span>
                </b-badge>
              </template>

              <template #cell(rank)="data">
                <b-badge
                  :variant="
                    data.item.results.rank == '1st' ||
                    data.item.results.rank == '2nd' ||
                    data.item.results.rank == '3rd'
                      ? 'light-success'
                      : 'light-primary'
                  "
                >
                  {{ data.item.results.rank }}
                </b-badge>
              </template>

              <template #cell(grade)="data">
                <b-badge
                  v-if="data.item.results.grade"
                  :variant="
                    data.item.results.grade.toLowerCase() == 'fail' ||
                    data.item.results.grade.toLowerCase() == 'failed' ||
                    data.item.results.grade.toLowerCase() == 'f'
                      ? 'light-danger'
                      : 'light-primary'
                  "
                >
                  {{ data.item.results.grade }}
                </b-badge>
              </template>
              <template #cell(result)="data">
                <b-badge
                  :variant="
                    data.item.results.result == 'PASSED'
                      ? 'light-success'
                      : 'light-danger'
                  "
                >
                  {{ data.item.results.result }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-button
                  v-if="!useMarksDetails"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                  @click="View(data.item)"
                >
                  <feather-icon icon="EyeIcon" class="" />
                </b-button>
                <b-button
                  v-else
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                  @click="View(data.item)"
                >
                  <feather-icon icon="EyeIcon" class="" />
                </b-button>
              </template>
            </b-table>
          </div>

          <b-card
            v-if="showMarksDetailsPanel"
            class="assessment-inline-card mt-2"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center">
                <b-button
                  variant="link"
                  class="p-0 mr-1 d-flex align-items-center"
                  @click="closeAssessmentPanel"
                >
                  <feather-icon icon="ArrowLeftIcon" size="16" class="mr-25" />
                  <span class="font-weight-bold text-primary">{{
                    $t("Back")
                  }}</span>
                </b-button>
                <h5 class="font-weight-bold mb-0 ml-1">
                  {{ assessmentModalTitle || $t("Assessment Details") }}
                </h5>
              </div>
            </div>
            <div class="d-flex flex-wrap assessment-filter-group mb-1">
              <b-button
                v-for="subject in assessmentSubjectFilters"
                :key="subject"
                size="sm"
                class="mr-50 mb-50 assessment-filter-btn"
                :variant="
                  activeAssessmentSubject === subject
                    ? 'primary'
                    : 'outline-primary'
                "
                @click="setAssessmentSubject(subject)"
              >
                {{ subject }}
              </b-button>
            </div>
            <b-table
              :items="filteredAssessmentRows"
              :fields="assessmentModalFields"
              small
              responsive
              show-empty
              :empty-text="$t('No assessment details found')"
              class="assessment-detail-table mb-1"
            >
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>
              <template #cell(subject)="data">
                <b-badge variant="light-primary">{{ data.value }}</b-badge>
              </template>
              <template #cell(date)="data">
                <b-badge variant="light-secondary">{{ data.value }}</b-badge>
              </template>
              <template #cell(exam)="data">
                <b-badge variant="light-info">{{ data.value }}</b-badge>
              </template>
              <template #cell(chapter)="data">
                <span class="font-weight-bold text-capitalize">{{
                  data.value
                }}</span>
              </template>
              <template #cell(score)="data">
                <b-badge variant="light-success">{{ data.value }}</b-badge>
              </template>
            </b-table>
          </b-card>

          <b-button
            v-if="marksGrid"
            variant="outline-secondary"
            class=""
            @click="marksGrid = false"
          >
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            <span class="align-middle">{{ $t("Back") }}</span>
          </b-button>

          <b-table
            v-if="marksGrid"
            responsive
            :items="marksData"
            :fields="marksFields"
            show-empty
            small
            class="mb-0 mt-2"
          >
            <template #head()="scope">
              <div>
                <span>{{ $t(scope.label) }}</span>
              </div>
            </template>
            <template #cell(subject)="data">
              <b-badge variant="light-primary" class="m-50">
                {{ data.value }}
              </b-badge>
            </template>

            <template #cell(attendance)="data">
              <b-badge
                v-if="data.item.marks.attendance"
                :variant="
                  data.item.marks.attendance == 'P'
                    ? 'light-success'
                    : 'light-danger'
                "
              >
                {{ data.item.marks.attendance }}
              </b-badge>
            </template>

            <template #cell(marks)="data">
              <b-badge variant="light-primary">
                {{ data.item.marks.obtained }} /
                {{ data.item.marks.total }}
              </b-badge>
            </template>

            <template #cell(grade)="data">
              <b-badge
                v-if="data.item.marks.meta_sub_grade"
                :variant="
                  data.item.marks.meta_sub_grade.toLowerCase() == 'fail' ||
                  data.item.marks.meta_sub_grade.toLowerCase() == 'failed' ||
                  data.item.marks.meta_sub_grade.toLowerCase() == 'f'
                    ? 'light-danger'
                    : 'light-primary'
                "
              >
                {{ data.item.marks.meta_sub_grade }}
              </b-badge>
            </template>

            <template #cell(percentage)="data">
              <b-badge variant="light-primary">
                {{ data.item.marks.meta_subj_percenatge }} <span>%</span>
              </b-badge>
            </template>

            <template #cell(rank)="data">
              <b-badge
                :variant="
                  data.item.marks.rank == '1st' ||
                  data.item.marks.rank == '2nd' ||
                  data.item.marks.rank == '3rd'
                    ? 'light-success'
                    : 'light-primary'
                "
              >
                {{ data.item.marks.rank }}
              </b-badge>
            </template>

            <template #cell(status)="data">
              <b-badge
                :variant="
                  data.item.marks.status == 'Cleared' ||
                  data.item.marks.status == 'PASSED'
                    ? 'light-success'
                    : 'light-danger'
                "
              >
                {{ data.item.marks.status }}
              </b-badge>
            </template>
          </b-table>

          <b-row align-h="end" class="mt-2">
            <b-col md="3">
              <!-- <v-select
                v-model="assess.subject"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="subjects"
                :clearable="false"
                ref="subject"
                @change="Check()"
                placeholder="Select subject"
              /> -->
            </b-col>
          </b-row>
        </b-card>
        <b-card no-body v-if="showChart">
          <b-card-header class="p-1">
            <div>
              <b-card-title>{{ $t("Assessment Summary") }}</b-card-title>
              <!-- <b-card-sub-title></b-card-sub-title> -->
            </div>
          </b-card-header>

          <!-- chart -->
          <b-card-body>
            <chartjs-component-line-chart
              :height="400"
              :data="lineChart.data"
              :options="lineChart.options"
            />
            <!-- :plugins="plugins" -->
          </b-card-body>
        </b-card>
      </b-tab>
      <b-tab :title="$t('PTM')" lazy>
        <template #title>
          <feather-icon icon="Edit2Icon" size="18" />
          <span class="tab-label">{{ $t("PTM") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body">
          <div v-if="!addPtm">
            <b-row>
              <b-col cols="12">
                <b-button variant="primary" @click="open()">
                  <feather-icon icon="PlusIcon" class="mr-50" />
                  <span class="align-middle">{{ $t("Summary") }}</span>
                </b-button>
                <b-button
                  class="ml-1"
                  variant="outline-primary"
                  @click="printPtm"
                  :disabled="ptmPrinting"
                >
                  <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
                  {{ $t("Print") }}
                </b-button>
              </b-col>
            </b-row>

            <div class="tab-data-table fee-data-table mt-1">
              <b-table
                responsive
                :items="ptmData"
                :fields="ptmFields"
                class="mb-0 tab-grid-table"
                sticky-header="420px"
                show-empty
                :busy="ptmLoading"
              >
                <template #head()="scope">
                  <div>
                    <span>{{ $t(scope.label) }}</span>
                  </div>
                </template>
                <template #table-busy>
                  <div
                    class="d-flex justify-content-center mb-3"
                    style="margin-top: 30px"
                  >
                    <b-spinner
                      style="width: 3rem; height: 3rem"
                      variant="primary"
                      :label="$t('Spinning')"
                    ></b-spinner>
                  </div>
                </template>
                <template #cell(date)="data">
                  <span class="table-date-chip">
                    {{ formatTableDate(data.value) }}
                  </span>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                    @click="Edit(data.item)"
                  >
                    <feather-icon icon="EditIcon" class="" />
                  </b-button>

                  <b-button
                    v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                    variant="outline-danger"
                    class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
                    @click="Delete(data.item.id)"
                  >
                    <feather-icon icon="Trash2Icon" />
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>
          <b-row
            v-else
            class="d-flex justify-content-between align-items-center"
          >
            <b-col md="3">
              <b-form-group
                :label="$t('Date')"
                :invalid-feedback="$t('Date is required')"
                ref="date"
              >
                <flat-pickr
                  :config="config"
                  v-model="ptmObj.date"
                  class="form-control"
                  :placeholder="$t('Select Date')"
                  @input="CheckDate()"
                />
              </b-form-group>
            </b-col>
            <b-col md="9">
              <b-form-group
                :label="$t('Summary')"
                :invalid-feedback="$t('Summary is required.')"
              >
                <b-form-input
                  :placeholder="$t('Enter summary.')"
                  ref="summary"
                  v-model="ptmObj.summary"
                  @focusout="CheckSummary()"
                />
              </b-form-group>
            </b-col>
            <b-col md="12" class="text-right">
              <b-button
                variant="outline-secondary"
                class="mr-50"
                @click="addPtm = false"
              >
                <feather-icon icon="ArrowLeftIcon" class="mr-50" />
                <span class="align-middle">{{ $t("Back") }}</span>
              </b-button>
              <b-button
                variant="success"
                @click="Add()"
                :disabled="request"
                class="mr-50"
              >
                <b-spinner v-if="request" small type="grow" />
                <span v-if="request == false">{{ $t("Save") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>

      <b-tab :title="$t('Siblings')" lazy @click="loadSiblings">
        <template #title>
          <feather-icon icon="GitBranchIcon" size="18" />
          <span class="tab-label">{{ $t("Siblings") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body siblings-panel" no-body>
          <div class="siblings-shell">
            <div class="siblings-header">
              <div class="siblings-header-left">
                <div class="siblings-header-icon">
                  <feather-icon icon="UsersIcon" size="20" />
                </div>
                <div>
                  <h5 class="mb-0 siblings-title">
                    {{ $t("Family Hierarchy") }}
                  </h5>
                  <p class="mb-0 siblings-subtitle">
                    {{ $t("Click a sibling to open their profile") }}
                  </p>
                </div>
              </div>
              <div class="siblings-header-badges">
                <span
                  v-if="siblingsFamilyCode"
                  class="siblings-pill siblings-pill--family"
                >
                  {{ $t("Family Code") }}: {{ siblingsFamilyCode }}
                </span>
                <span
                  v-if="!siblingsLoading"
                  class="siblings-pill siblings-pill--count"
                >
                  {{ siblingsList.length }}
                  {{
                    siblingsList.length === 1 ? $t("Sibling") : $t("Siblings")
                  }}
                </span>
              </div>
            </div>

            <div v-if="siblingsLoading" class="siblings-state text-center py-5">
              <b-spinner
                variant="primary"
                style="width: 3rem; height: 3rem"
                :label="$t('Loading')"
              />
              <p class="mt-2 mb-0 text-muted">
                {{ $t("Loading siblings...") }}
              </p>
            </div>

            <div
              v-else-if="!siblingsList.length"
              class="siblings-state siblings-empty text-center py-5"
            >
              <div class="siblings-empty-icon mb-1">
                <feather-icon icon="UsersIcon" size="34" />
              </div>
              <h6 class="mb-50">{{ $t("No siblings found") }}</h6>
              <p class="mb-0 text-muted small">
                {{ $t("No other students share this family code.") }}
              </p>
            </div>

            <div v-else class="siblings-tree-wrap">
              <div class="siblings-tree">
                <article
                  v-for="(sibling, index) in siblingsList"
                  :key="sibling.id || index"
                  class="siblings-tree-node"
                  :class="'siblings-tone-' + (index % 4)"
                  role="button"
                  tabindex="0"
                  @click="openSiblingProfile(sibling)"
                  @keyup.enter="openSiblingProfile(sibling)"
                >
                  <div class="siblings-branch">
                    <span class="siblings-branch-stem" />
                    <span class="siblings-branch-elbow" />
                    <span class="siblings-branch-dot" />
                  </div>

                  <div class="siblings-card">
                    <div class="siblings-avatar-wrap">
                      <b-avatar
                        :src="getSiblingPicture(sibling)"
                        size="48px"
                        variant="light-primary"
                        class="siblings-avatar"
                      >
                        <feather-icon
                          v-if="!getSiblingPicture(sibling)"
                          icon="UserIcon"
                          size="18"
                        />
                      </b-avatar>
                    </div>

                    <div class="siblings-card-info">
                      <div class="siblings-name-row">
                        <h6 class="mb-0 siblings-name">
                          {{ sibling.name || "—" }}
                        </h6>
                        <span
                          class="siblings-status"
                          :class="getSiblingStatusClass(sibling.status)"
                        >
                          {{ sibling.status || "—" }}
                        </span>
                      </div>

                      <div class="siblings-meta-row">
                        <span v-if="sibling.grNo" class="siblings-meta-chip">
                          <feather-icon icon="HashIcon" size="12" />
                          GR {{ sibling.grNo }}
                        </span>
                        <span class="siblings-meta-chip">
                          <feather-icon icon="BookOpenIcon" size="12" />
                          {{
                            sibling.meta_class ||
                            [sibling.className, sibling.sectionName]
                              .filter(Boolean)
                              .join(" - ") ||
                            "—"
                          }}
                        </span>
                        <span class="siblings-meta-chip">
                          <feather-icon icon="CalendarIcon" size="12" />
                          {{ formatSiblingDob(sibling.date_of_birth) }}
                          <template v-if="sibling.age != null">
                            · {{ sibling.age }} yrs
                          </template>
                        </span>
                        <span v-if="sibling.gender" class="siblings-meta-chip">
                          <feather-icon icon="UserIcon" size="12" />
                          {{ sibling.gender }}
                        </span>
                        <span v-if="sibling.campus" class="siblings-meta-chip">
                          <feather-icon icon="HomeIcon" size="12" />
                          {{ sibling.campus }}
                        </span>
                      </div>

                      <div v-if="sibling.contacts" class="siblings-contacts">
                        <feather-icon icon="PhoneIcon" size="12" />
                        <span>{{ sibling.contacts }}</span>
                      </div>
                    </div>

                    <div class="siblings-open-hint">
                      <feather-icon icon="ExternalLinkIcon" size="16" />
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </b-card>
      </b-tab>

      <b-tab
        v-if="shouldShowMedicalTab"
        :title="$t('Medical Report')"
        lazy
        @click="loadMedicalRecords"
      >
        <template #title>
          <feather-icon icon="ActivityIcon" size="18" />
          <span class="tab-label">{{ $t("Medical Report") }}</span>
        </template>
        <b-card class="content-panel tab-panel-body">
          <b-row class="mb-2">
            <b-col cols="12" class="text-right">
              <b-button
                variant="outline-primary"
                @click="printMedicalReport"
                :disabled="medicalLoading || medicalRecords.length === 0"
              >
                <feather-icon icon="PrinterIcon" size="16" class="mr-1" />
                {{ $t("Print") }}
              </b-button>
            </b-col>
          </b-row>

          <div class="tab-data-table fee-data-table">
            <b-table
              responsive
              :items="medicalRecords"
              :fields="medicalFields"
              class="mb-0 tab-grid-table"
              sticky-header="420px"
              show-empty
              :busy="medicalLoading"
              :empty-text="$t('No medical records found')"
            >
              <template #head()="scope">
                <div>
                  <span>{{ $t(scope.label) }}</span>
                </div>
              </template>
              <template #table-busy>
                <div class="d-flex justify-content-center mb-3 mt-3">
                  <b-spinner
                    variant="primary"
                    type="grow"
                    :label="$t('Spinning')"
                  ></b-spinner>
                </div>
              </template>

              <template #cell(sno)="{ index }">
                <span class="text-muted">{{ index + 1 }}</span>
              </template>

              <template #cell(reportedBy)="{ item }">
                <span>{{ item.reportedBy || "N/A" }}</span>
              </template>

              <template #cell(symptoms)="{ item }">
                <div
                  class="text-truncate"
                  style="max-width: 150px"
                  :title="item.symptoms || 'N/A'"
                >
                  {{ item.symptoms || "N/A" }}
                </div>
              </template>

              <template #cell(actionTakenAtSchool)="{ item }">
                <div
                  class="text-truncate"
                  style="max-width: 150px"
                  :title="item.actionTakenAtSchool || 'N/A'"
                >
                  {{ item.actionTakenAtSchool || "N/A" }}
                </div>
              </template>

              <template #cell(medicationGiven)="{ item }">
                <div
                  class="text-truncate"
                  style="max-width: 120px"
                  :title="item.medicationGiven || 'N/A'"
                >
                  {{ item.medicationGiven || "N/A" }}
                </div>
              </template>

              <template #cell(observation)="{ item }">
                <div
                  class="text-truncate"
                  style="max-width: 120px"
                  :title="item.observation || 'N/A'"
                >
                  {{ item.observation || "N/A" }}
                </div>
              </template>

              <template #cell(parentActionRequired)="{ item }">
                <b-badge
                  :variant="
                    item.parentActionRequired === 'Yes' ? 'danger' : 'success'
                  "
                >
                  {{ item.parentActionRequired || "N/A" }}
                </b-badge>
              </template>

              <template #cell(remarks)="{ item }">
                <span>{{ item.remarks || "N/A" }}</span>
              </template>

              <template #cell(date)="{ item }">
                <span class="table-date-chip">{{
                  formatMedicalDate(item.date)
                }}</span>
              </template>
            </b-table>
          </div>
        </b-card>
      </b-tab>
      <b-tab :title="$t('Transfer History')" lazy @click="loadTransferHistory">
        <template #title>
          <feather-icon icon="RepeatIcon" size="18" />
          <span class="tab-label">{{ $t("Transfer History") }}</span>
        </template>
        <div
          class="tab-content-wrapper scrollable-tab-content animation-fade transfer-history-shell"
        >
          <div v-if="transferHistoryLoading" class="text-center py-5">
            <b-spinner
              variant="primary"
              style="width: 3rem; height: 3rem"
              :label="$t('Loading')"
            />
          </div>
          <ul
            v-else-if="transferHistoryLogs.length"
            class="transfer-history-timeline vuexy-timeline list-unstyled mt-1 mb-0"
          >
            <li
              v-for="(log, index) in transferHistoryLogs"
              :key="log.id || index"
              class="timeline-item"
              :class="getHistoryCardClass(log.logCategory)"
            >
              <span
                class="timeline-point"
                :class="getHistoryPointClass(log.logCategory)"
              >
                <feather-icon
                  :icon="getHistoryIcon(log.logCategory)"
                  size="16"
                />
              </span>
              <div
                class="timeline-event history-card animation-fade"
                :class="getHistoryCardClass(log.logCategory)"
              >
                <div class="history-card__body">
                  <div class="history-card__top">
                    <b-badge
                      v-if="!isHappiness"
                      :variant="getHistoryBadgeVariant(log.logCategory)"
                      class="history-card__badge"
                    >
                      <feather-icon
                        :icon="getHistoryIcon(log.logCategory)"
                        size="13"
                      />
                      {{ formatLogCategory(log.logCategory) }}
                    </b-badge>
                    <span class="history-card__date">
                      <feather-icon icon="CalendarIcon" size="13" />
                      {{ formatHistoryDate(log.updatedOn) }}
                    </span>
                  </div>

                  <template v-if="isHappiness">
                    <template
                      v-if="
                        getParsedHistoryLog(log).changes &&
                        getParsedHistoryLog(log).changes.length > 0
                      "
                    >
                      <div
                        v-for="(change, cIdx) in getParsedHistoryLog(log)
                          .changes"
                        :key="'ch-' + cIdx"
                        class="history-change-row d-flex align-items-center flex-wrap"
                      >
                        <span
                          v-if="change.label"
                          class="history-field-label text-muted font-weight-bolder mr-75"
                        >
                          {{ $t(change.label) }}:
                        </span>

                        <div class="d-inline-flex align-items-center flex-wrap">
                          <span
                            class="history-pill history-pill--prev"
                            :class="{
                              'history-pill--muted': !change.isChanged,
                            }"
                          >
                            {{ change.from }}
                          </span>

                          <feather-icon
                            icon="ArrowRightIcon"
                            size="16"
                            class="history-arrow-icon mx-75"
                          />

                          <span
                            class="history-pill history-pill--next"
                            :class="
                              getHistoryThemeClass(log.logCategory, 'pill')
                            "
                          >
                            {{ change.to }}
                          </span>
                        </div>
                      </div>
                    </template>
                    <p
                      v-else-if="getParsedHistoryLog(log).text"
                      class="history-card__details mb-0"
                    >
                      {{ getParsedHistoryLog(log).text }}
                    </p>
                    <p v-else class="history-card__details mb-0">
                      {{ log.details || $t("No notes provided.") }}
                    </p>
                  </template>

                  <template v-else>
                    <div
                      v-if="getHistoryRoute(log.details)"
                      class="history-card__route"
                    >
                      <span class="history-card__route-from">
                        {{ getHistoryRoute(log.details).from }}
                      </span>
                      <feather-icon
                        icon="ArrowRightIcon"
                        size="15"
                        class="history-card__route-arrow"
                      />
                      <span class="history-card__route-to">
                        {{ getHistoryRoute(log.details).to }}
                      </span>
                    </div>
                    <p v-else class="history-card__details mb-0">
                      {{ log.details || $t("No notes provided.") }}
                    </p>
                  </template>

                  <div
                    v-if="
                      !isHappiness && (log.metaCampusName || log.metaUpdatedBy)
                    "
                    class="history-card__meta"
                  >
                    <span v-if="log.metaCampusName" class="history-meta-chip">
                      <feather-icon icon="MapPinIcon" size="13" />
                      {{ log.metaCampusName }}
                    </span>
                    <span v-if="log.metaUpdatedBy" class="history-meta-chip">
                      <feather-icon icon="UserIcon" size="13" />
                      {{ log.metaUpdatedBy }}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          </ul>
          <div v-else class="text-center p-5 empty-state-box">
            <feather-icon
              icon="RepeatIcon"
              size="50"
              class="text-muted mb-2 empty-state-icon"
            />
            <h4 class="empty-state-title">
              {{ $t("No Transfer History Available") }}
            </h4>
            <p class="empty-state-message mb-0">
              {{
                $t(
                  "This student doesn't have any transfer or class change history yet."
                )
              }}
            </p>
          </div>
        </div>
      </b-tab>
      <!-- <b-tab title="Resoruces">
        <template #title>
          <feather-icon icon="BookIcon" size="18" />
          <span style="font-size: 18px">Resoruces</span>
        </template>
        <b-card>
          <b-card-title>Resources</b-card-title>
          <b-table responsive :items="resources" class="mb-0 mt-2">
            <template #cell(subject)="data">
              <b-badge variant="light-primary">
                {{ data.value }}
              </b-badge>
            </template>
            <template #cell(notes)="data">
              <b-button variant="primary" class="btn-icon">
                <feather-icon icon="FileIcon" />
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-tab> -->
    </b-tabs>
  </div>
  <!-- <b-card
    no-body
    class="border-primary"
  >
    <b-card-header class="d-flex justify-content-between align-items-center pt-75 pb-25">
      <h5 class="mb-0">
        Current Plan
      </h5>
      <b-badge variant="light-primary">
        Basic
      </b-badge>
      <small class="text-muted w-100">July 22, 2021</small>
    </b-card-header>

    <b-card-body>
      <ul class="list-unstyled my-1">
        <li>
          <span class="align-middle">5 Users</span>
        </li>
        <li class="my-25">
          <span class="align-middle">10 GB storage</span>
        </li>
        <li>
          <span class="align-middle">Basic Support</span>
        </li>
      </ul>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
      >
        Upgrade Plan
      </b-button>
    </b-card-body>
  </b-card> -->
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BButton,
  BTab,
  BTabs,
  BRow,
  BCol,
  BTable,
  BFormCheckbox,
  BCardTitle,
  BFormGroup,
  BFormInput,
  BSpinner,
  BSidebar,
  BForm,
  BFormTextarea,
  BAvatar,
  BMedia,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import UserViewUserTimelineCard from "./UserViewUserTimelineCard.vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import ChartjsComponentLineChart from "@/views/charts-and-maps/charts/chartjs/charts-components/ChartjsComponentLineChart.vue";
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";
import { $themeColors } from "@themeConfig";

export default {
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },

  components: {
    BCard,
    BSidebar,
    BForm,
    BBadge,
    BCardTitle,
    BCardHeader,
    BCardBody,
    BButton,
    BTab,
    BTabs,
    BRow,
    BCol,
    BTable,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BSpinner,
    BFormTextarea,
    BAvatar,
    BMedia,
    UserViewUserTimelineCard,
    vSelect,
    flatPickr,
    ChartjsComponentLineChart,
  },
  props: {
    dues: {
      type: Array,
      required: true,
    },
    history: {
      type: Array,
      required: true,
    },
    duesTotal: {
      type: Number,
      required: true,
    },
    userData: {
      type: Object,
      required: true,
    },
    gridLoading: {
      type: Boolean,
    },
    showReceive: {
      type: Boolean,
    },
    campuses: {
      type: Array,
      required: true,
    },
    getCampus: {
      type: Function,
      required: true,
    },
  },
  data() {
    // colors
    const chartColors = {
      primaryColorShade: "#836AF9",
      yellowColor: "#ffe800",
      successColorShade: "#28dac6",
      warningColorShade: "#ffe802",
      warningLightColor: "#FDAC34",
      infoColorShade: "#299AFF",
      greyColor: "#4F5D70",
      blueColor: "#2c9aff",
      blueLightColor: "#84D0FF",
      greyLightColor: "#EDF1F4",
      tooltipShadow: "rgba(0, 0, 0, 0.25)",
      lineChartPrimary: "#666ee8",
      lineChartDanger: "#ff4961",
      labelColor: "#6e6b7b",
      grid_line_color: "rgba(200, 200, 200, 0.2)",
    };
    return {
      color: ["primary", "success", "warning", "info", "danger", "secondary"],
      lineChart: {
        options: {
          responsive: true,
          maintainAspectRatio: false,
          backgroundColor: false,
          hover: {
            mode: "label",
          },
          tooltips: {
            // Updated default tooltip UI
            shadowOffsetX: 1,
            shadowOffsetY: 1,
            shadowBlur: 8,
            shadowColor: chartColors.tooltipShadow,
            // backgroundColor: $themeColors.white,
            titleFontColor: "#fff",
            bodyFontColor: "#fff",
          },
          layout: {
            padding: {
              top: -15,
              bottom: -25,
              left: -15,
            },
          },
          scales: {
            xAxes: [
              {
                display: true,
                scaleLabel: {
                  display: true,
                },
                gridLines: {
                  display: true,
                  color: chartColors.grid_line_color,
                  zeroLineColor: chartColors.grid_line_color,
                },
                ticks: {
                  fontColor: chartColors.labelColor,
                },
              },
            ],
            yAxes: [
              {
                display: true,
                scaleLabel: {
                  display: true,
                },
                // ticks: {
                //   stepSize: 10000,
                //   min: 0,
                //   max: 50000,
                //   fontColor: chartColors.labelColor,
                // },
                gridLines: {
                  display: true,
                  color: chartColors.grid_line_color,
                  zeroLineColor: chartColors.grid_line_color,
                },
              },
            ],
          },
          legend: {
            display: false,
            position: "top",
            align: "start",
            labels: {
              usePointStyle: true,
              padding: 25,
              boxWidth: 9,
            },
          },
        },
        data: {
          labels: [],
          // "Math", "Science", "History", "English"
          datasets: [
            // {
            //   label: "Term 1",
            //   backgroundColor: "rgba(75, 192, 192, 0.2)",
            //   borderColor: "rgba(75, 192, 192, 1)",
            //   data: [85, 90, 78, 88], // Term 1 results per subject,
            //   fill: false,
            // },
            // {
            //   label: "Term 2",
            //   backgroundColor: "rgba(153, 102, 255, 0.2)",
            //   borderColor: "rgba(153, 102, 255, 1)",
            //   data: [80, 85, 82, 90], // Term 2 results per subject,
            //   fill: false,
            // },
            // {
            //   label: "Term 3",
            //   backgroundColor: "rgba(255, 159, 64, 0.2)",
            //   borderColor: "rgba(255, 159, 64, 1)",
            //   data: [88, 92, 80, 85], // Term 3 results per subject,
            //   fill: false,
            // },
            // {
            //   data: [
            //     80, 125, 105, 130, 215, 195, 140, 160, 230, 300, 220, 170, 210,
            //     // 200, 280,
            //   ],
            //   label: "English",
            //   borderColor: $themeColors.success,
            //   lineTension: 0.5,
            //   pointStyle: "circle",
            //   backgroundColor: $themeColors.success,
            //   fill: false,
            //   pointRadius: 1,
            //   pointHoverRadius: 5,
            //   pointHoverBorderWidth: 5,
            //   pointBorderColor: "transparent",
            //   pointHoverBorderColor: $themeColors.white,
            //   pointHoverBackgroundColor: chartColors.success,
            //   pointShadowOffsetX: 1,
            //   pointShadowOffsetY: 1,
            //   pointShadowBlur: 5,
            //   pointShadowColor: chartColors.tooltipShadow,
            // },
            // {
            //   data: [
            //     80, 150, 180, 270, 210, 160, 160, 202, 265, 210, 270, 255, 290,
            //     360, 375,
            //   ],
            //   label: "Urdu",
            //   borderColor: $themeColors.danger,
            //   lineTension: 0.5,
            //   pointStyle: "circle",
            //   backgroundColor: chartColors.lineChartDanger,
            //   fill: false,
            //   pointRadius: 1,
            //   pointHoverRadius: 5,
            //   pointHoverBorderWidth: 5,
            //   pointBorderColor: "transparent",
            //   pointHoverBorderColor: $themeColors.white,
            //   pointHoverBackgroundColor: chartColors.lineChartDanger,
            //   pointShadowOffsetX: 1,
            //   pointShadowOffsetY: 1,
            //   pointShadowBlur: 5,
            //   pointShadowColor: chartColors.tooltipShadow,
            // },
          ],
        },
      },
      marksChart: [],
      showChart: false,
      feesFields: [
        { label: "fee type", key: "feeType" },
        { label: "details", key: "monthYear" },
        { label: "balance", key: "balance" },
      ],
      subjectFeeFields: [
        { label: "subject", key: "subjectName" },
        { label: "teacher", key: "teacherName" },
        { label: "amount", key: "subjectFee" },
        { label: "share", key: "teacherShareAmount" },
      ],
      subjectFees: [],
      subjectFeesLoading: false,
      subjectFeesLoaded: false,
      subjectTotalFee: 0,
      attFields: [
        { label: "date", key: "date" },
        { label: "status", key: "status" },
        { label: "Time In / Time Out", key: "timeInOut" },
      ],
      compFields: [
        { label: "date", key: "date" },
        { label: "from", key: "name" },
        { label: "complaint", key: "complaint" },
        { label: "actions", key: "actions" },
      ],
      parentFields: [
        { label: "date", key: "date" },
        { label: "for", key: "name" },
        { label: "complaint", key: "complaint" },
        { label: "actions", key: "parActions" },
      ],
      ptmFields: [
        { label: "date", key: "date" },
        { label: "summary", key: "summary" },
        "actions",
      ],
      assess: {
        subject: "",
      },
      subjects: ["English", "Urdu", "Mathhematics"],
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      config2: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        mode: "range",
      },
      attendance: [],
      attendanceDurationFilter: "",
      complaints: [],
      byparent: false,
      compObj: {
        id: 0,
        sID: this.$route.params.id,
        tID: 0,
        date: "",
        complaint: "",
        status: "pending",
        attachment: "",
        campusID: this.$store.state.userData.cId,
      },
      resObj: {},
      visibility: false,
      sidebarTitle: "",
      buttonTitle: "",
      mydocloading: false,
      mediaLoaded: false,
      visibility2: false,
      ptmData: [],
      ptmObj: {
        id: 0,
        campusID: this.$store.state.userData.cId,
        sID: this.$route.params.id,
        date: "",
        summary: "",
      },
      addPtm: false,
      request: false,
      ptmLoading: false,
      // Medical Records Data
      medicalRecords: [],
      medicalLoading: false,
      siblingsList: [],
      siblingsLoading: false,
      siblingsFamilyCode: null,
      siblingsLoaded: false,
      transferHistoryLogs: [],
      transferHistoryLoading: false,
      showMedicalTab: false,
      medicalFields: [
        { key: "sno", label: "S.No", sortable: false },
        { key: "date", label: "Date", sortable: false },
        { key: "reportedBy", label: "Reported By", sortable: false },
        { key: "symptoms", label: "Symptoms", sortable: false },
        { key: "actionTakenAtSchool", label: "Action Taken", sortable: false },
        { key: "medicationGiven", label: "Medication", sortable: false },
        { key: "observation", label: "Observation", sortable: false },
        {
          key: "parentActionRequired",
          label: "Parent Action",
          sortable: false,
        },
        { key: "remarks", label: "Remarks", sortable: false },
      ],
      dataLoading: false,
      examTypes: [],
      resultFields: [
        { label: "exam", key: "examType" },
        { label: "session", key: "session" },
        { label: "marks", key: "marks" },
        { label: "percentage", key: "percentage" },
        { label: "rank", key: "rank" },
        { label: "grade", key: "grade" },
        { label: "result", key: "result" },
        "actions",
      ],
      assessments: [],
      marksData: [],
      marksGrid: false,
      marksFields: [
        { label: "subject", key: "subject" },
        { label: "attendance", key: "attendance" },
        { label: "marks", key: "marks" },
        { label: "percentage", key: "percentage" },
        { label: "rank", key: "rank" },
        { label: "grade", key: "grade" },
        { label: "status", key: "status" },
      ],
      useMarksDetails: false,
      expandedAssessmentId: null,
      assessmentModalTitle: "",
      assessmentSubjectFilters: [],
      activeAssessmentSubject: "All",
      assessmentRows: [],
      assessmentModalFields: [
        { key: "subject", label: "subject" },
        { key: "date", label: "date" },
        { key: "exam", label: "exam" },
        { key: "chapter", label: "chapter/topics" },
        { key: "score", label: "score" },
      ],
      // fees: [
      //   {
      //     feeType: "Tution Fee",
      //     date: "9 Feb 2023",
      //     amount: 2000,
      //   },
      //   {
      //     feeType: "Annual Fee",
      //     date: "5 Jan 2023",
      //     amount: 5500,
      //   },
      // ],
      resources: [
        {
          subject: "English",
          notes: "",
        },
        {
          subject: "Physics",
          notes: "",
        },
      ],
      visibility3: false,
      visibility4: false,
      visibility5: false,
      feeObj: {
        sID: 0,
        year: 0,
        userID: this.$store.state.userData.userID,
        feesTypeIDs: [],
        months: [],
      },
      years: [
        2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026,
        2027, 2028, 2029, 2030,
      ],
      months: [
        { text: "January", value: "Jan" },
        { text: "February", value: "Feb" },
        { text: "March", value: "Mar" },
        { text: "April", value: "Apr" },
        { text: "May", value: "May" },
        { text: "June", value: "Jun" },
        { text: "July", value: "Jul" },
        { text: "August", value: "Aug" },
        { text: "September", value: "Sep" },
        { text: "October", value: "Oct" },
        { text: "November", value: "Nov" },
        { text: "December", value: "Dec" },
      ],
      saving: false,
      selectedID: [],
      selectedFeeType: [],
      regFeeIDs: [],
      printing: false,
      accTypes: [],
      recObj: {
        grNo: "",
        familyCode: 0,
        amount: 0,
        rcvDate: "",
        userID: this.$store.state.userData.userID,
        campusID: this.$store.state.userData.cId,
        feesIDs: [],
        accountID: 0,
      },
      totalAmount: 0,
      receiving: false,
      admObj: null,
      instSaving: false,
      instObj: {},
      instCurrent: 0,
      disObj: {},
      visibility6: false,
      currentBalance: 0,
      netBalance: 0,
      savingDis: false,
      chDetails: null,
      orgList: [],
      visibility7: false,
      orgFields: [
        { label: "Name", key: "title" },
        { key: "actions", label: "actions" },
      ],
      otherObj: {
        organization: 0,
        bank: "",
        cheque: "",
      },
      orgObj: {},
      savingOrg: false,
      exempting: false,
      exObj: {},
      visibility8: false,
      chObj: {},
      chOptions: [],
      chClasses: [],
      chSections: [],
      clsLoading: false,
      transfering: false,
      showExDate: false,
      cHeadObj: null,
      holdSett: null,
      showHold: false,
      holdObj: {},
      holdSaving: false,
      unholding: false,
      currLedgObj: null,
      rights: {},
      reportObj: {
        dtFrom: null,
        dtTo: null,
        rangeDate: "",
      },
      customFeesDateSetting: null,
      customFeesDateForm: {
        issueDate: null,
        dueDate: null,
        expiryDate: null,
      },
      customFeesDateLoadedRecord: null,
      loadingCustomFeesDateLoad: false,
      loadingCustomFeesDateModal: false,
      removingCustomFeesDate: false,
      savingCustomFeesDate: false,
      feeSettings: {
        id: 0,
        campusID: 0,
        issueDate: null,
        dueDate: null,
        expiryDate: null,
        lateFee: 0,
        timelyDiscount: 0,
        printSlip: false,
        sendSMS: false,
        instructions: "",
        bankName: "",
        accountNumber: "",
        printBy: "student",
        titleOfAccount: "",
      },
      attObj: null,
      attSaving: false,
      ptmPrinting: false,
    };
  },
  created() {
    this.$route.params.id = parseInt(this.$route.params.id);
    this.rights = this.$store.state.rights["manage-studentgrid"];
    this.LoadSettings();
    this.LoadSettingsAll();
    this.loadAttendance("");
    this.loadComplaints();
    // this.loadExamCat();
    // this.LoadMarkGraph();
    this.loadPtm();
    this.loadAccounts();
    if (
      this.reportDomain === "myskoolhpgs" &&
      ["registered", "enrolled"].includes(this.userData.status.toLowerCase())
    ) {
      this.feesFields.push({
        label: "actions",
        key: "actions",
        tdClass: "d-flex",
      });
    }

    if (this.remarksVisible) {
      this.attFields.push({ label: "Remarks", key: "remarks" });
    }

    this.showExDate = this.reportDomain === "myskoolhpgs";
    // &&  this.$store.state.loggedUser.campusID == 1;

    // Set medical tab visibility based on report domain
    this.showMedicalTab =
      this.reportDomain === "myskoolelixir" || this.reportDomain === "myskool";
  },
  watch: {
    isAnySidebarOpen(open) {
      document.body.classList.toggle("user-view-sidebar-open", !!open);
    },
    "userData.id"() {
      this.subjectFees = [];
      this.subjectFeesLoaded = false;
      this.subjectTotalFee = 0;
    },
  },
  beforeDestroy() {
    document.body.classList.remove("user-view-sidebar-open");
  },
  computed: {
    reportDomain() {
      const fromStore = String(this.$store.state.report || "")
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
      if (fromStore && fromStore !== "reports") return fromStore;

      const host = String(window.location.hostname || "").toLowerCase();
      if (host.includes("myskoolskh")) return "myskoolskh";

      const sub = host.split(".")[0] || "";
      if (sub.startsWith("myskool")) return sub;

      return fromStore;
    },
    isSkhDomain() {
      return this.reportDomain === "myskoolskh";
    },
    subjectFeeRows() {
      return this.subjectFees || [];
    },
    // Computed property for medical tab visibility
    shouldShowMedicalTab() {
      return (
        this.reportDomain === "myskoolelixir" || this.reportDomain === "myskool"
      );
    },
    isHappiness() {
      return this.reportDomain == "myskoolhappinessfoundation";
    },
    remarksVisible() {
      return ["myskooliota", "myskooliota2"].includes(this.reportDomain);
    },
    filteredAssessmentRows() {
      if (this.activeAssessmentSubject === "All") {
        return this.assessmentRows;
      }
      return this.assessmentRows.filter(
        (row) => row.subject === this.activeAssessmentSubject
      );
    },
    showMarksDetailsPanel() {
      return this.useMarksDetails && !!this.expandedAssessmentId;
    },
    isCustomFeesDateEnabled() {
      const s = this.customFeesDateSetting;
      if (!s || s.key !== "custom_fees_date") return false;
      if (s.valueBool === true || s.valueBool === 1) return true;
      if (typeof s.valueBool === "string") {
        const t = s.valueBool.toLowerCase().trim();
        return t === "true" || t === "1";
      }
      return false;
    },
    isAnySidebarOpen() {
      return (
        this.visibility ||
        this.visibility2 ||
        this.visibility3 ||
        this.visibility4 ||
        this.visibility5 ||
        this.visibility6 ||
        this.visibility7 ||
        this.visibility8 ||
        this.showHold
      );
    },
    customFeesDateSelectedDescriptors() {
      const ids = this.selectedID || [];
      const out = [];
      if (!Array.isArray(ids) || !ids.length) return out;

      ids.forEach((fid) => {
        const row = this.dues.find(
          (it) => it.fees && Number(it.fees.id) === Number(fid)
        );
        if (!row) {
          out.push({
            fid,
            badgeLabel: `${this.$t("Fee")} #${fid}`,
          });
          return;
        }
        const feeName = row.feesType?.feeType || this.$t("Fee");
        const period = (row.fees?.monthYear || "").trim();
        const badgeLabel = period ? `${feeName} ${period}` : feeName;
        out.push({ fid, badgeLabel });
      });
      return out;
    },
    customFeesDateHasSavedRecord() {
      const r = this.customFeesDateLoadedRecord;
      return !!(r && r.id);
    },
  },
  methods: {
    ...mapActions({
      get: "get",
      post: "post",
      put: "put",
      delete: "delete",
      postObj: "postObj",
    }),

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    getStudentPictureForPrint() {
      const raw =
        this.userData?.picture ||
        this.userData?.avatar ||
        this.userData?.image ||
        this.userData?.photo ||
        this.userData?.profileImage ||
        "";
      return this.getPicturePreview(raw);
    },

    getVariant(st) {
      const vt =
        st == "P"
          ? "success"
          : st == "A" || st == "AA"
          ? "danger"
          : st == "L" || st == "H" || st == "SL"
          ? "info"
          : st == "Off"
          ? "secondary"
          : "warning";

      return `light-${vt}`;
    },

    formatTime(timeValue) {
      if (!timeValue) {
        return "-";
      }

      try {
        const time = new Date(timeValue);
        if (isNaN(time.getTime())) {
          return "-";
        }

        return new Intl.DateTimeFormat("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }).format(time);
      } catch (error) {
        return "-";
      }
    },
    async fetchDate() {
      var obj = {
        url: this.$store.state.domain + "Fees/GetDate",
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);

      // console.log(res);
      if (res && !Array.isArray(res)) {
        return res;
      } else {
        return new Date().toJSON();
      }
    },

    onAttSelect(row) {
      // console.log(row);
      if (row && row.length > 0) {
        this.attObj = { ...row[0] };
        this.$bvModal.show("modal-remarks");
      }
    },
    async editAtt() {
      if (!this.attObj.meta_notes) {
        this.$bvToast.toast("Please enter the remarks", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      this.attSaving = true;
      var status = await this.put({
        url:
          this.$store.state.domain +
          "Attendance/" +
          this.attObj.id +
          "?db=" +
          this.$store.state.userData.db,
        body: this.attObj,
        message: "Remarks updated successfully.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.attSaving = false;
      if (status) {
        this.$bvModal.hide("modal-remarks");
        this.$refs.attendanceTable.clearSelected();
        this.loadAttendance(this.attendanceDurationFilter);
      }
    },

    openReport() {
      const fd = new Date();
      const firstDate = `${fd.getFullYear()}-${(fd.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-01`;

      const ld = new Date(fd.getFullYear(), fd.getMonth() + 1, 0);
      const lastDate = `${ld.getFullYear()}-${(ld.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${ld.getDate().toString().padStart(2, "0")}`;

      // console.log(firstDate, lastDate);
      this.reportObj = {
        dtFrom: firstDate,
        dtTo: lastDate,
        rangeDate: `${firstDate} to ${lastDate}`,
      };

      this.$bvModal.show("modal-att-report");
    },
    setRepDate(sd, dateStr) {
      // console.log(sd, dateStr);
      if (dateStr) {
        if (sd.length > 1) {
          let date = dateStr.split(" to ");
          if (date.length == 1) {
            this.reportObj.dtFrom = date[0];
            this.reportObj.dtTo = date[0];
          } else {
            this.reportObj.dtFrom = date[0];
            this.reportObj.dtTo = date[1];
          }
        } else {
          this.reportObj.dtFrom = dateStr;
          this.reportObj.dtTo = null;
        }
      } else {
        this.reportObj.dtFrom = null;
        this.reportObj.dtTo = null;
      }
      // console.log(this.repObj);
    },
    getReport() {
      if (this.reportObj.dtFrom && this.reportObj.dtTo) {
        let url =
          `https://${this.reportDomain}.myskool.app/Attendance/Show?type=student-wise&dtFrom=` +
          this.reportObj.dtFrom +
          "&dtTo=" +
          this.reportObj.dtTo +
          "&dbb=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&cls=&sec=&status=&sID=" +
          this.$route.params.id;

        window.open(url, "_blank");
      } else {
        this.$bvToast.toast("Please select the date range", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },
    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID,
        body: ["admission_feeIDs", "challan_with_header"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.admObj = res.find((el) => el.key == "admission_feeIDs");
      this.cHeadObj = res.find((el) => el.key == "challan_with_header");
    },
    async LoadSettingsAll() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db,
        body: [
          "cheque_details",
          "hold_from_view",
          "show_student_ledger",
          "student-deposit",
          "marks-details",
          "custom_fees_date",
        ],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.chDetails = res.find((el) => el.key == "cheque_details");
      this.holdSett = res.find((el) => el.key == "hold_from_view");
      this.customFeesDateSetting = res.find(
        (el) =>
          el &&
          String(el.key || "")
            .trim()
            .toLowerCase() === "custom_fees_date"
      );

      this.currLedgObj = res.find((el) => el.key == "show_student_ledger");
      const val = this.currLedgObj && this.currLedgObj.valueBool;

      let depositObj = res.find((el) => el.key == "student-deposit");
      const val2 = depositObj && depositObj.valueBool;

      // pass value to parent to update prop value
      const settingValues = { showLedger: val, showDeposit: val2 };
      this.$emit("update-settings", settingValues);
      // console.log(settingValues);
      const marksDetailSetting = res.find((el) => el.key == "marks-details");
      this.useMarksDetails =
        (marksDetailSetting?.value || marksDetailSetting?.valueText || "")
          .toString()
          .toLowerCase() === "marks-details";

      if (this.chDetails && this.chDetails.valueBool) this.LoadOrg();
      await this.loadResult();
    },
    async loadAttendance(filter) {
      this.attendanceDurationFilter =
        filter === undefined || filter === null ? "" : String(filter);
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Students/LoadAttendance?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          this.$route.params.id +
          "&duration=" +
          filter,

        token: this.$store.state.userData.token,
      };
      this.attendance = await this.get(obj);
      // console.log("att:", this.attendance);
      this.dataLoading = false;
    },

    formatAttendanceExportDate(dateVal) {
      if (dateVal == null || dateVal === "") return "";
      try {
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return String(dateVal);
        return d.toLocaleDateString("en-UK", {
          year: "numeric",
          day: "numeric",
          month: "short",
        });
      } catch {
        return String(dateVal);
      }
    },

    attendanceExportTimeRange(item) {
      if (!item || ["A", "H"].includes(item.status)) return "-";
      if (item.timeIn || item.timeOut) {
        return `${this.formatTime(item.timeIn)} - ${this.formatTime(
          item.timeOut
        )}`;
      }
      return "-";
    },

    escapeCsvField(val) {
      const s = String(val ?? "");
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    },

    exportAttendanceCsv() {
      const rows = Array.isArray(this.attendance) ? this.attendance : [];
      if (!rows.length) {
        this.$bvToast.toast(
          this.$t("No attendance records for this range.") ||
            "No attendance records for this range.",
          {
            title: this.$t("Export"),
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      const headers = this.attFields.map((f) => this.$t(f.label));
      const lines = [
        headers.map((h) => this.escapeCsvField(h)).join(","),
        ...rows.map((row) =>
          [
            this.escapeCsvField(this.formatAttendanceExportDate(row.date)),
            this.escapeCsvField(row.status != null ? row.status : ""),
            this.escapeCsvField(this.attendanceExportTimeRange(row)),
          ].join(",")
        ),
      ];
      const csv = "\uFEFF" + lines.join("\r\n");

      const rawName = this.userData.name || "Student";
      const safeName = rawName
        .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_")
        .trim()
        .substring(0, 80);
      const dur = String(this.attendanceDurationFilter || "all").replace(
        /\s+/g,
        "_"
      );
      const sid = this.$route.params.id;
      const dayStamp = new Date().toISOString().slice(0, 10);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        `Attendance_${safeName}_${sid}_${dur}_${dayStamp}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      this.$bvToast.toast(
        this.$t("Attendance exported successfully.") ||
          "Attendance exported successfully.",
        {
          title: this.$t("Export"),
          variant: "success",
          solid: true,
        }
      );
    },
    async loadComplaints() {
      this.byparent = false;
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Students/LoadComplaints?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          this.$route.params.id,

        token: this.$store.state.userData.token,
      };
      this.complaints = await this.get(obj);
      // console.log("com:", this.complaints);
      this.dataLoading = false;
    },
    async loadParent() {
      this.byparent = true;
      this.dataLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "SchoolComplaints/LoadDataStudent?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID +
          "&sID=" +
          this.$route.params.id,

        token: this.$store.state.userData.token,
      };
      this.complaints = await this.get(obj);
      // console.log("par", this.complaints);
      this.dataLoading = false;
    },
    async loadAccounts() {
      var obj2 = {
        url:
          this.$store.state.domain +
          "CollectionAccounts?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID,
        token: this.$store.state.userData.token,
      };
      let result = await this.get(obj2);
      // console.log("acc", result);
      if (result !== "NotFound") this.accTypes = result;
      else this.accTypes = [];
      if (
        this.reportDomain === "myskoolhpgs" &&
        this.$store.state.loggedUser.campusID != 1
      ) {
        // show only cash and cheque
        this.accTypes = this.accTypes.filter((el) => [18, 20].includes(el.id));
      }
      // console.log(this.$store.state.loggedUser);
    },
    async LoadOrg() {
      var obj = {
        url:
          this.$store.state.domain +
          "DropDownList/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID +
          "&category=org",
        token: this.$store.state.userData.token,
      };
      this.orgList = await this.get(obj);
      // console.log(this.orgList);
    },
    openOrg() {
      this.orgObj = {
        id: 0,
        title: "",
        typee: "org",
        campusID: this.userData.campusID,
      };
      this.visibility7 = true;
      var elem = this.$refs["org_name"];
      elem.state = undefined;
    },
    checkOrg() {
      var elem = this.$refs["org_name"];
      if (this.orgObj.title == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveOrg() {
      if (this.checkOrg() == true) {
        this.savingOrg = true;
        if (this.orgObj.id == 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "DropDownList?db=" +
              this.$store.state.userData.db,
            body: this.orgObj,
            message: "Organization added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          var status = await this.put({
            url:
              this.$store.state.domain +
              "DropDownList/" +
              this.orgObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Organization updated successfully.",
            context: this,
            body: this.orgObj,
            token: this.$store.state.userData.token,
          });
        }
        if (status) {
          this.LoadOrg();
          this.orgObj = {
            id: 0,
            title: "",
            typee: "org",
            campusID: this.userData.campusID,
          };
          var elem = this.$refs["org_name"];
          elem.state = undefined;
        }
        this.savingOrg = false;
      }
    },
    async editOrg(acc) {
      this.orgObj = { ...acc };
      var elem = this.$refs["org_name"];
      elem.state = undefined;
    },
    async deleteOrg(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "DropDownList/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Organization deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadOrg();
      }
    },

    setSelectAll(e) {
      // console.log(e);
      if (e) {
        this.$refs.dueTable.selectAllRows();
      } else {
        this.$refs.dueTable.clearSelected();
      }
    },
    setSelected(e, ind) {
      if (e) {
        this.$refs.dueTable.selectRow(ind);
      } else {
        this.$refs.dueTable.unselectRow(ind);
      }
      // console.log(e, ind);
    },
    async onRowSelect(ch) {
      // console.log(ch);
      this.totalAmount = ch.reduce((total, c) => total + c.fees.balance, 0);
      this.selectedID = ch.reduce((acc, c) => {
        acc.push(c.fees.id);
        return acc;
      }, []);

      this.selectedFeeType = ch.reduce((acc, c) => {
        acc.push(c.fees.feesType);
        return acc;
      }, []);
      if (this.isCustomFeesDateEnabled) {
        await this.fetchCustomFeesDateLoadData({ silent: true });
      }
      // console.log(this.selectedFeeType);
    },
    async print() {
      if (this.dues.length > 0) {
        this.printing = true;
        try {
          if (this.isCustomFeesDateEnabled) {
            await this.fetchCustomFeesDateLoadData({ silent: true });
            await this.openFeeSettings(false);
          }

          let controller = "GenerateChallanStudentLeft";
          let contr = "ChallanLeft";
          if (this.userData.status.toLowerCase() == "present") {
            controller = "GenerateChallanStudent";
            contr = "Challan";
          }
          var pObj = {
            fCode: 0,
            sID: this.$route.params.id,
            cID: [],
            secID: [],
            feeTypeID: [],
            months: [],
            year: 0,
            familyWise: true,
            userID: this.$store.state.userData.userID,
            feeIDs: this.selectedID,
          };
          var objData = await this.postObj({
            url:
              this.$store.state.domain +
              "feesChallan/" +
              controller +
              "?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.userData.campusID +
              "&exp=false",
            body: pObj,
            message: null,
            context: this,
            token: this.$store.state.userData.token,
          });

          let header = this.cHeadObj ? this.cHeadObj.valueBool : false;

          if (
            this.reportDomain === "myskooliuss" &&
            this.userData.status &&
            this.userData.status.toLowerCase() === "registered"
          ) {
            header = true;
          }

          let dueDate = new Date().toJSON().split("T")[0];
          if (this.isCustomFeesDateEnabled && this.feeSettings.dueDate) {
            dueDate =
              this.normalizeChallanDateForApi(this.feeSettings.dueDate) ||
              dueDate;
          }

          if (objData.length > 0) {
            let url =
              `https://${this.reportDomain}.myskool.app/Fee/${contr}?dbb=` +
              this.$store.state.userData.db +
              "&sID=" +
              this.$route.params.id +
              "&cID=" +
              this.userData.campusID +
              "&cls=&sec=&feeType=&fCode=-1&fcID=" +
              objData +
              "&duedate=" +
              dueDate +
              "&header=" +
              header;

            window.open(url, "_blank");
          } else {
            this.$bvToast.toast("Nothing to print.", {
              title: this.$t("Error!"),
              variant: "danger",
              toaster: "b-toaster-top-center",
            });
          }
        } finally {
          this.printing = false;
        }
      } else {
        this.$bvToast.toast("No dues found", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },

    normalizeFeeIdsList(val) {
      if (val == null || val === "") return [];
      if (Array.isArray(val)) {
        return val.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
      }
      return String(val)
        .split(",")
        .map((s) => Number(String(s).trim()))
        .filter((n) => !Number.isNaN(n));
    },

    shouldPassCustomFeesParamsToFeeSettingsLoad() {
      if (!this.isCustomFeesDateEnabled) return false;
      if (!this.userData?.id) return false;

      const rec = this.customFeesDateLoadedRecord;
      const recId = rec != null ? Number(rec.id) : NaN;
      if (!rec || Number.isNaN(recId) || recId <= 0) return false;

      const selected = this.normalizeFeeIdsList(this.selectedID);
      if (!selected.length) return false;

      const saved = this.normalizeFeeIdsList(
        rec.feeIDs ?? rec.feesIDs ?? rec.feeIDsStr
      );
      if (saved.length) {
        const a = [...selected].sort((x, y) => x - y).join(",");
        const b = [...saved].sort((x, y) => x - y).join(",");
        if (a !== b) return false;
      }

      return true;
    },

    buildFeeSettingsLoadDataUrl(includeCustomFeesContext = true) {
      const campusID =
        this.userData.campusID || this.$store.state.userData.cId || 0;
      const params = new URLSearchParams({
        campusID: String(campusID),
        db: this.$store.state.userData.db,
      });

      if (
        includeCustomFeesContext &&
        this.shouldPassCustomFeesParamsToFeeSettingsLoad()
      ) {
        const ids = this.normalizeFeeIdsList(this.selectedID);
        const fc = Number(this.userData.familyCode);
        params.set("feeIDs", ids.join(","));
        params.set("sID", String(this.userData.id));
        params.set("familyCode", String(Number.isFinite(fc) ? fc : 0));
        params.set("fcID", String(this.customFeesDateLoadedRecord.id));
      }

      return (
        this.$store.state.domain + "FeeSettings/LoadData?" + params.toString()
      );
    },

    async openFeeSettings(showPanel) {
      const myitem = await this.get({
        url: this.buildFeeSettingsLoadDataUrl(showPanel !== true),
        token: this.$store.state.userData.token,
      });
      if (myitem == null || myitem == "") {
        this.feeSettings = {
          id: 0,
          campusID: this.userData.campusID || this.$store.state.userData.cId,
          issueDate: null,
          dueDate: null,
          expiryDate: null,
          lateFee: 0,
          timelyDiscount: 0,
          printSlip: false,
          sendSMS: false,
          instructions: "",
          bankName: "",
          accountNumber: "",
          printBy: "student",
          titleOfAccount: "",
        };
      } else {
        this.feeSettings = myitem;
      }
    },

    normalizeChallanDateForApi(val) {
      if (val == null || val === "") return null;
      const s = String(val);
      if (s.includes("T")) return s.slice(0, 10);
      return s.length >= 10 ? s.slice(0, 10) : s;
    },

    resetCustomFeesDateForm() {
      this.customFeesDateForm = {
        issueDate: null,
        dueDate: null,
        expiryDate: null,
      };
    },

    pickCustomFeesDateRow(list) {
      if (!Array.isArray(list) || !list.length) return null;
      const usable = list.find((r) => {
        const st = String(r.status || "active")
          .toLowerCase()
          .trim();
        return st !== "delete" && st !== "deleted";
      });
      return usable || list[0] || null;
    },

    async fetchCustomFeesDateLoadData(options = {}) {
      const silent = !!options.silent;
      this.customFeesDateLoadedRecord = null;

      if (!this.isCustomFeesDateEnabled) return;
      if (!this.userData?.id) return;
      const ids = this.selectedID || [];
      if (!ids.length) return;

      const feeIDsStr = [...ids]
        .map((id) => Number(id))
        .filter((n) => !Number.isNaN(n))
        .join(",");
      if (!feeIDsStr) return;

      if (!silent) this.loadingCustomFeesDateLoad = true;
      try {
        const campusID =
          this.userData.campusID || this.$store.state.userData.cId || 0;
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          campusID: String(campusID),
          feeIDs: feeIDsStr,
          sID: String(this.userData.id),
        });
        const url =
          this.$store.state.domain +
          "CustomFeesDate/LoadData?" +
          params.toString();
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          returnData: true,
        });
        if (!res || res === false) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const rec = this.pickCustomFeesDateRow(list);
        const recId = rec != null ? Number(rec.id) : NaN;
        this.customFeesDateLoadedRecord =
          rec != null && !Number.isNaN(recId) && recId > 0 ? rec : null;
      } catch (e) {
        console.warn("CustomFeesDate/LoadData", e);
        this.customFeesDateLoadedRecord = null;
      } finally {
        if (!silent) this.loadingCustomFeesDateLoad = false;
      }
    },

    fillCustomFeesDateFormFromCampusDefaults() {
      this.customFeesDateForm.issueDate = this.normalizeChallanDateForApi(
        this.feeSettings.issueDate
      );
      this.customFeesDateForm.dueDate = this.normalizeChallanDateForApi(
        this.feeSettings.dueDate
      );
      this.customFeesDateForm.expiryDate = this.normalizeChallanDateForApi(
        this.feeSettings.expiryDate
      );
    },

    fillCustomFeesDateFormFromLoadedRecord(rec) {
      const r = rec || this.customFeesDateLoadedRecord;
      if (!r) return false;
      const issueDate = this.normalizeChallanDateForApi(r.issueDate);
      const dueDate = this.normalizeChallanDateForApi(r.dueDate);
      const expiryDate = this.normalizeChallanDateForApi(r.expiryDate);
      if (!issueDate || !dueDate || !expiryDate) return false;
      this.customFeesDateForm.issueDate = issueDate;
      this.customFeesDateForm.dueDate = dueDate;
      this.customFeesDateForm.expiryDate = expiryDate;
      return true;
    },

    async openCustomFeesDateModal() {
      if (!this.isCustomFeesDateEnabled) return;
      if (!this.userData?.id) {
        this.$bvToast.toast(this.$t("Please select the student first"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      if (!this.selectedID.length) {
        this.$bvToast.toast(
          this.$t("Please select one or more fee rows first."),
          {
            title: this.$t("Error!"),
            variant: "warning",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }
      this.loadingCustomFeesDateModal = true;
      try {
        await this.fetchCustomFeesDateLoadData({ silent: true });
        await this.openFeeSettings(false);
        if (!this.fillCustomFeesDateFormFromLoadedRecord()) {
          this.fillCustomFeesDateFormFromCampusDefaults();
        }
      } finally {
        this.loadingCustomFeesDateModal = false;
      }
      this.$bvModal.show("modal-custom-fees-date");
    },

    async removeCustomFeesDate() {
      if (!this.isCustomFeesDateEnabled) return;
      const id = this.customFeesDateLoadedRecord?.id;
      if (id == null || id === "") return;
      const result = await Swal.fire({
        title: this.$t("Remove custom fee dates?"),
        text: this.$t("This will clear saved dates for this fee selection."),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: this.$t("Yes, remove"),
        cancelButtonText: this.$t("Cancel"),
      });
      if (!result.isConfirmed) return;

      const campusID =
        this.userData.campusID || this.$store.state.userData.cId || 0;
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        id: String(id),
        campusID: String(campusID),
        userID: String(this.$store.state.userData.userID),
      });
      this.removingCustomFeesDate = true;
      try {
        const ok = await this.put({
          url:
            this.$store.state.domain +
            "CustomFeesDate/RemoveDate?" +
            params.toString(),
          body: {},
          message: this.$t("Custom fee dates removed."),
          context: this,
          token: this.$store.state.userData.token,
        });
        if (ok) {
          this.customFeesDateLoadedRecord = null;
          await this.openFeeSettings(false);
          this.fillCustomFeesDateFormFromCampusDefaults();
          this.$bvModal.hide("modal-custom-fees-date");
        }
      } finally {
        this.removingCustomFeesDate = false;
      }
    },

    async saveCustomFeesDate() {
      if (!this.isCustomFeesDateEnabled) return;
      const issueDate = this.normalizeChallanDateForApi(
        this.customFeesDateForm.issueDate
      );
      const dueDate = this.normalizeChallanDateForApi(
        this.customFeesDateForm.dueDate
      );
      const expiryDate = this.normalizeChallanDateForApi(
        this.customFeesDateForm.expiryDate
      );
      if (!issueDate || !dueDate || !expiryDate) {
        this.$bvToast.toast(
          this.$t("Please select issue, due and expiry dates."),
          {
            title: this.$t("Validation"),
            variant: "warning",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }
      const feeIDs = (this.selectedID || []).map((id) => Number(id));
      if (!feeIDs.length || feeIDs.some((n) => Number.isNaN(n))) {
        this.$bvToast.toast(this.$t("Invalid fee selection."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      const campusID =
        this.userData.campusID || this.$store.state.userData.cId || 0;
      const fc = Number(this.userData.familyCode);
      const recordId = Number(this.customFeesDateLoadedRecord?.id);
      const payload = {
        id: Number.isFinite(recordId) && recordId > 0 ? recordId : 0,
        feeIDs,
        campusID,
        issueDate,
        expiryDate,
        dueDate,
        sID: this.userData.id || 0,
        familyCode: Number.isFinite(fc) ? fc : 0,
      };
      this.savingCustomFeesDate = true;
      try {
        const status = await this.post({
          url:
            this.$store.state.domain +
            "CustomFeesDate/Save?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: payload,
          message: this.$t("Custom fee dates saved successfully."),
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          await this.fetchCustomFeesDateLoadData({ silent: true });
          await this.openFeeSettings(false);
          this.$bvModal.hide("modal-custom-fees-date");
        }
      } finally {
        this.savingCustomFeesDate = false;
      }
    },

    openHold() {
      this.holdObj = {
        date: null,
        reason: "",
        feeIDs: this.selectedID,
        userID: this.$store.state.userData.userID,
      };
      this.showHold = true;
    },
    CheckHoldDate() {
      var elem = this.$refs["hold_date"];
      if (!this.holdObj.date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveHold() {
      if (this.CheckHoldDate() == true) {
        this.holdSaving = true;
        // console.log(this.holdObj);
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/HoldFeeSelected?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.userData.campusID,
          body: this.holdObj,
          message: "Fees holded successfully",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.showHold = false;
          this.$refs.dueTable.clearSelected();
          this.$emit("load-dues");
        }
        this.holdSaving = false;
      }
    },
    async unHoldFee() {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: "Do you want to unhold the selected fee?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Unhold it!",
      });

      if (result.isConfirmed) {
        this.unholding = true;
        let obj = {
          feeIDs: this.selectedID,
          campusID: this.userData.campusID,
          userID: this.$store.state.userData.userID,
        };
        // console.log(obj);
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/UnholdFeeSelected?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.userData.campusID,
          body: obj,
          message: "Fees unholded successfully",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.unholding = false;
        if (status) {
          this.$refs.dueTable.clearSelected();
          this.$emit("load-dues");
        }
      }
    },

    async openInit() {
      let cls = this.userData.cls_current;
      if (cls && cls.status == "active") {
        this.$refs.dueTable.clearSelected();

        let ids = [];

        // For myskoolhpgs, only use settings (don't use name-based fallback)
        if (this.reportDomain === "myskoolhpgs") {
          if (this.admObj && this.admObj.value) {
            ids = this.admObj.value.split(",").map((el) => parseInt(el));
          } else {
            Swal.fire({
              title: this.$t("Error"),
              text: "Admission fee settings not configured.",
              icon: "error",
            });
            return;
          }
        }
        // For myskooliuss, try settings first, then fallback to name-based search
        else if (this.reportDomain === "myskooliuss") {
          // Try to use admObj from settings if available and valid
          if (
            this.admObj &&
            this.admObj.value &&
            this.admObj.value.trim() !== ""
          ) {
            const settingIds = this.admObj.value
              .split(",")
              .map((el) => parseInt(el.trim()))
              .filter((id) => !isNaN(id) && id > 0);
            if (settingIds.length > 0) {
              ids = settingIds;
            } else {
              // Settings exist but no valid IDs, fallback to name-based search
              console.log(
                "Settings exist but no valid IDs, using name-based search for myskooliuss"
              );
            }
          }

          // If admObj is not set or has no valid value, find fees dynamically by name
          if (ids.length === 0) {
            try {
              let feeTypesRes = await this.get({
                url:
                  this.$store.state.domain +
                  "FeeType/LoadAll?db=" +
                  this.$store.state.userData.db +
                  "&cId=" +
                  this.userData.campusID,
                token: this.$store.state.userData.token,
              });

              // Find fee types by name: admission, security deposit, and tuition fee
              console.log(
                "All fee types loaded:",
                feeTypesRes.map((ft) => ft.feeType)
              );
              let relevantFees = feeTypesRes.filter((ft) => {
                if (!ft.feeType) return false;
                const feeTypeLower = ft.feeType.toLowerCase().trim();
                const matches =
                  feeTypeLower.includes("admission") ||
                  feeTypeLower.includes("security") ||
                  feeTypeLower.includes("deposit") ||
                  feeTypeLower.includes("tuition");
                if (matches) {
                  console.log("Matched fee type:", ft.feeType, "ID:", ft.id);
                }
                return matches;
              });

              if (relevantFees.length > 0) {
                ids = relevantFees.map((ft) => ft.id);
                console.log(
                  "Found fees for myskooliuss by name:",
                  relevantFees.map((ft) => ({ name: ft.feeType, id: ft.id }))
                );

                // For registered students in myskooliuss, ensure tuition fee is included
                if (
                  this.userData.status &&
                  this.userData.status.toLowerCase() === "registered"
                ) {
                  const tuitionFee = relevantFees.find(
                    (ft) =>
                      ft.feeType && ft.feeType.toLowerCase().includes("tuition")
                  );
                  if (tuitionFee && !ids.includes(tuitionFee.id)) {
                    ids.push(tuitionFee.id);
                    console.log(
                      "Added Tuition Fee for registered student:",
                      tuitionFee.feeType
                    );
                  }
                }
              } else {
                Swal.fire({
                  title: this.$t("Error"),
                  text: "No admission, security deposit, or tuition fee types found. Please configure fee settings.",
                  icon: "error",
                });
                return;
              }
            } catch (error) {
              console.error("Error loading fee types:", error);
              Swal.fire({
                title: this.$t("Error"),
                text: "Failed to load fee types. Please try again.",
                icon: "error",
              });
              return;
            }
          }
        } else {
          // For other domains, use settings only
          if (this.admObj && this.admObj.value) {
            ids = this.admObj.value.split(",").map((el) => parseInt(el));
          } else {
            Swal.fire({
              title: this.$t("Error"),
              text: "Admission fee settings not configured.",
              icon: "error",
            });
            return;
          }
        }

        if (ids.length === 0) {
          Swal.fire({
            title: this.$t("Error"),
            text: "No fee types found.",
            icon: "error",
          });
          return;
        }

        this.visibility3 = true;
        this.feeObj = {
          sID: this.$route.params.id,
          year: new Date().getFullYear(),
          userID: this.$store.state.userData.userID,
          feesTypeIDs: ids,
          // months: this.months[new Date().getMonth()].value,
          months: [],
        };
        var elem = this.$refs["month"];
        elem.state = undefined;
        // console.log(this.feeObj);
      } else {
        Swal.fire({
          title: "Inactive Class",
          text: "Please transfer the student first.",
          icon: "warning",
        });
      }
    },
    selectMonth(val) {
      let obj = this.feeObj.months.find((el) => el == val);
      if (obj) {
        this.feeObj.months = this.feeObj.months.filter((el) => el != val);
      } else this.feeObj.months.push(val);
    },
    CheckMonths() {
      var elem = this.$refs["month"];
      // this.feeObj.months == ""
      if (this.feeObj.months.length == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckYear() {
      var elem = this.$refs["year"];
      if (this.feeObj.year == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveFee() {
      this.CheckMonths();
      this.CheckYear();
      if (this.CheckMonths() == false || this.CheckYear() == false) {
        return this.$bvToast.toast("Please select the required fields", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else {
        // this.feeObj.months = [this.feeObj.months];
        // console.log(this.feeObj);
        this.saving = true;
        // "Fees/CreateFee?db=" +
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/CreateFeeInitial?db=" +
            this.$store.state.userData.db,
          body: this.feeObj,
          message: "fee created successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.saving = false;
        if (status) {
          this.visibility3 = false;
          this.$emit("load-dues");
        }
      }
    },
    async openRec() {
      if (this.selectedID.length > 0) {
        this.visibility4 = true;
        let cashAcc = this.accTypes.find(
          (el) => el.account.toLowerCase() == "cash"
        );

        this.recObj = {
          grNo: this.userData.grNo,
          familyCode: 0,
          amount: this.totalAmount,
          rcvDate: new Date().toJSON().split("T")[0],
          userID: this.$store.state.userData.userID,
          campusID: this.userData.campusID,
          feesIDs: this.selectedID,
          accountID: cashAcc ? cashAcc.id : 0,
          receiveNote: "",
          barcode: 0,
        };
        this.recObj.rcvDate = await this.fetchDate();
        // console.log(this.recObj);

        // filter the registration fee from dues
        this.regFeeIDs = this.dues
          .filter((el) =>
            el.feesType.feeType.toLowerCase().includes("registration")
          )
          .map((el) => el.fees.feesType);
        console.log(this.regFeeIDs);
      } else {
        this.regFeeIDs = [];
        this.$bvToast.toast("Please select the receiving fee", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
    },
    CheckAcc() {
      var elem = this.$refs["acc"];
      if (this.recObj.accountID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckRcvDate() {
      var elem = this.$refs["rcvDate"];
      if (!this.recObj.rcvDate) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckRcNote() {
      var elem = this.$refs["rec_note"];
      if (this.recObj.receiveNote.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckRcptNo() {
      var elem = this.$refs["rcptNo"];
      let x = parseInt(this.recObj.barcode);
      if (isNaN(x) || x <= 0) {
        return (elem.state = false);
      } else {
        this.recObj.barcode = x;
        return (elem.state = true);
      }
    },
    async receiveFee() {
      if (
        this.CheckAcc() == true &&
        this.CheckRcvDate() == true &&
        this.CheckRcNote() == true &&
        this.CheckRcptNo() == true
      ) {
        if (this.totalAmount > 0) {
          this.receiving = true;
          if (this.chDetails && this.chDetails.valueBool) {
            this.recObj.feesChequeDetails = {
              id: 0,
              billID: 0,
              organizationID: this.otherObj.organization,
              bank: this.otherObj.bank,
              cheque: this.otherObj.cheque,
              otherDetails: "",
              campusID: this.userData.campusID,
            };
          } else this.recObj.feesChequeDetails = null;

          // "Fees/ReceiveFees?db=" +
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Fees/ReceiveFeesNew?db=" +
              this.$store.state.userData.db +
              "&withLate=false",
            body: this.recObj,
            message: "Fee received successfully.",
            context: this,
            token: this.$store.state.userData.token,
            feeReceive: true,
          });
          this.receiving = false;
          // console.log(status);
          if (status) {
            if (this.userData.status.toLowerCase() == "registered") {
              this.checkFee(
                this.selectedFeeType,
                this.regFeeIDs,
                this.recObj.rcvDate
              );
            }

            this.visibility4 = false;
            // let rem = this.duesTotal - this.totalAmount;
            // let url =
            //   `https://${this.reportDomain}.myskool.app/Fee/Slip?dbb=` +
            //   this.$store.state.userData.db +
            //   "&bID=" +
            //   status +
            //   "&cID=" +
            //   this.userData.campusID +
            //   "&rem=" +
            //   rem;
            // window.open(url, "_blank");
            this.$refs.dueTable.clearSelected();
            this.$emit("load-dues");
          }
        } else {
          this.$bvToast.toast("Receiving amount is 0", {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        }
      }
    },
    checkFee(item, regFee, dt) {
      let fIDs = [...item]; // selected fee ids
      let regFeeIDs = [...regFee]; // registration fee ids

      let sameIDs = []; // same ids in selected fee and initiated setting fee values
      let regSameIDs = []; // same ids in selected fee and registration fee

      if (this.admObj && this.admObj.value) {
        let ids = this.admObj.value.split(",").map((el) => parseInt(el));

        sameIDs = ids.filter((el) => fIDs.includes(el));
        // console.log(sameIDs);
      }
      if (sameIDs.length > 0) this.updateStudent("enrolled", dt);
      else if (regFeeIDs.length > 0) {
        regSameIDs = regFeeIDs.filter((el) => fIDs.includes(el));
        if (regSameIDs.length > 0) this.updateStudent("registered", dt);
      }
    },
    async updateStudent(st, dt) {
      if (st == "registered") {
        // registration fee is received
        this.userData.date_of_registraion = dt;
      } else {
        // initiated setting fee values
        this.userData.status = "enrolled";
        this.userData.date_of_admission = dt;
      }

      var status = await this.put({
        url:
          this.$store.state.domain +
          "students/" +
          this.userData.id +
          "?db=" +
          this.$store.state.userData.db,
        body: this.userData,
        message: "Student marked as enrolled.",
        context: this,
        token: this.$store.state.userData.token,
      });
      if (status && st == "enrolled") {
        const enRights = this.$store.state.rights["student-enrolled"];
        if (enRights) {
          this.$router.push({ name: "student-enrolled" });
        }
      }
    },

    openInst(item) {
      // console.log(item);
      this.instObj = {
        feeID: item.fees.id,
        amount: null,
        campusID: this.userData.campusID,
      };
      this.instCurrent = item.fees.balance;
      this.$refs["instAmount"].state = undefined;
      this.visibility5 = true;
    },
    checkInstallment() {
      var elem = this.$refs["instAmount"];
      let x = parseInt(this.instObj.amount);
      if (isNaN(x) || x <= 0 || x >= this.instCurrent) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveInstallment() {
      if (this.checkInstallment() == true) {
        this.instObj.amount = parseInt(this.instObj.amount);
        // console.log(this.instObj);
        this.instSaving = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/BreakFee?db=" +
            this.$store.state.userData.db,
          body: this.instObj,
          message: "Installment added successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.instSaving = false;
        if (status) {
          this.visibility5 = false;
          this.$refs.dueTable.clearSelected();
          this.$emit("load-dues");
        }
      }
    },
    openDiscount(item) {
      // console.log(item);
      this.disObj = {
        feesID: item.fees.id,
        discountAmount: item.fees.discount,
        userID: this.$store.state.userData.userID,
        timelyDiscount: 0,
        discountNote: item.fees.discountNote,
      };
      this.currentBalance = item.fees.actualFee;
      this.netBalance = item.fees.actualFee - item.fees.discount;
      this.$refs["discount"].state = undefined;
      this.visibility6 = true;
    },
    checkDiscount() {
      var elem = this.$refs["discount"];
      let x = parseInt(this.disObj.discountAmount);
      if (isNaN(x) || x >= this.currentBalance) {
        return (elem.state = false);
      } else {
        this.disObj.discountAmount = x;
        return (elem.state = true);
      }
    },
    validateDiscount() {
      if (this.checkDiscount() == true) {
        this.netBalance = this.currentBalance - this.disObj.discountAmount;
      } else {
        this.netBalance = this.currentBalance;
      }
    },
    async AddDiscount() {
      if (this.checkDiscount() == false) {
        return this.$bvToast.toast("Please enter the discount amount.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-right",
        });
      } else {
        this.disObj.discountAmount = parseInt(this.disObj.discountAmount);
        // console.log(this.disObj);
        this.savingDis = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/AddDiscount?db=" +
            this.$store.state.userData.db,
          body: this.disObj,
          message: "Discount added successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.savingDis = false;
        if (status) {
          this.visibility6 = false;
          this.$refs.dueTable.clearSelected();
          this.$emit("load-dues");
        }
      }
    },
    async openExempt(id) {
      this.exObj = {
        feeID: id,
        reason: "",
        date: null,
      };
      this.$bvModal.show("modal-exempt");
      if (this.showExDate) {
        this.exObj.date = await this.fetchDate();
        // console.log(this.exObj.date);
      }
    },
    checkExReason() {
      var elem = this.$refs["ex_reason"];
      if (this.exObj.reason == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkExDate() {
      if (this.showExDate) {
        var elem = this.$refs["ex_date"];
        if (!this.exObj.date) {
          return (elem.state = false);
        } else {
          return (elem.state = true);
        }
      } else return true;
    },
    async ExemptWithNote() {
      if (this.checkExReason() == true && this.checkExDate() == true) {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "Do you want to exempt this fee?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes!",
        });

        if (result.isConfirmed) {
          // console.log(this.exObj);
          this.exempting = true;
          let dtParam = this.showExDate ? "&date=" + this.exObj.date : "";

          var status = await this.post({
            url:
              this.$store.state.domain +
              "Fees/ExemptFee?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.userData.campusID +
              "&feeID=" +
              this.exObj.feeID +
              "&reason=" +
              this.exObj.reason +
              "&userID=" +
              this.$store.state.userData.userID +
              dtParam,
            message: "Fee exempt successfully.",
            context: this,
            body: null,
            token: this.$store.state.userData.token,
          });
          this.exempting = false;
          if (status) {
            this.$bvModal.hide("modal-exempt");
            this.$refs.dueTable.clearSelected();
            this.$emit("load-dues");
          }
        }
      }
    },
    async Exempt(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: "Do you want to exempt this fee?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes!",
      });

      if (result.isConfirmed) {
        var status = await this.post({
          url:
            this.$store.state.domain +
            "Fees/ExemptFee?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.userData.campusID +
            "&feeID=" +
            id,
          message: "Fee exempt successfully.",
          context: this,
          body: null,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.$refs.dueTable.clearSelected();
          this.$emit("load-dues");
        }
      }
    },

    openCh() {
      this.chObj = {
        ...this.userData,
        newCampus: 0,
        clsID: 0,
        secID: 0,
      };

      this.visibility8 = true;
      this.chSections = [];
      this.chClasses = [];
      this.chOptions = [];

      // console.log(this.chObj);
    },
    async LoadClass() {
      this.checkChCamp();
      this.clsLoading = true;
      this.chObj.clsID = 0;
      this.chObj.secID = 0;
      this.chSections = [];

      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.chObj.newCampus +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      //  this.items =[];
      let myitem = await this.get(obj);
      this.chClasses = myitem;
      this.chOptions = this.chClasses.map((el) => el.cls);

      this.clsLoading = false;
    },
    FillChSec() {
      this.chObj.secID = 0;
      this.chSections = [];
      let obj = this.chClasses.find((el) => el.cls.id == this.chObj.clsID);
      if (obj && obj.sections.length > 0) {
        this.chSections = obj.sections;
      } else this.chSections = [];

      this.checkChClass();
    },
    checkChCamp() {
      var elem = this.$refs["ch_camp"];
      if (this.chObj.newCampus == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkChClass() {
      var elem = this.$refs["ch_cls"];
      if (this.chObj.clsID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkChSec() {
      var elem = this.$refs["ch_sec"];
      if (this.chObj.secID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveCh() {
      this.checkChCamp();
      this.checkChClass();
      this.checkChSec();
      if (
        this.checkChCamp() == false ||
        this.checkChClass() == false ||
        this.checkChSec() == false
      ) {
        this.$bvToast.toast(this.$t("Please select all the required fields"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      // else if (this.chObj.campusID == this.chObj.newCampus) {
      //   this.$bvToast.toast("Current campus and transfer campus are same", {
      //     title: this.$t("Error!"),
      //     variant: "danger",
      //     toaster: "b-toaster-top-center",
      //   });
      // }
      else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "Do you want to transfer the student?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes!",
        });

        if (result.isConfirmed) {
          this.transfering = true;
          let data = {
            sID: this.chObj.id,
            previousCampus: this.chObj.campusID,
            newCampus: this.chObj.newCampus,
            clsID: this.chObj.clsID,
            secID: this.chObj.secID,
            userID: this.$store.state.userData.userID,
          };
          // console.log(data);

          let status = await this.post({
            url:
              this.$store.state.domain +
              "Students/TransferCampus?db=" +
              this.$store.state.userData.db,
            body: data,
            message: "Student transferred successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.transfering = false;
          if (status) {
            this.visibility8 = false;
            this.$router.go();
          }
        }
      }
    },

    openComp() {
      this.compObj = {
        id: 0,
        sID: this.$route.params.id,
        tID: 0,
        date: "",
        complaint: "",
        status: "pending",
        attachment: "",
        campusID: this.userData.campusID,
      };
      this.sidebarTitle = "Add Complaint";
      this.buttonTitle = "Save";
      this.visibility = true;
      this.mediaLoaded = false;

      var elem = this.$refs["complaint"];
      elem.state = undefined;
      var elem = this.$refs["date"];
      elem.state = undefined;
    },
    CheckComp() {
      var elem = this.$refs["complaint"];
      if (this.compObj.complaint == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckCompDate() {
      var elem = this.$refs["compDate"];
      if (this.compObj.date == "" || this.compObj.date == null) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    removeAtt() {
      this.compObj.attachment = null;
      this.mediaLoaded = false;
      this.$refs.attachment.files = null;
    },

    async uploadAtt() {
      if (this.$refs.attachment.files.length !== 0) {
        this.mydocloading = true;
        this.mediaLoaded = false;
        try {
          const uploaded = await uploadToMySkool([
            this.$refs.attachment.files[0],
          ]);
          this.compObj.attachment = getUploadPath(uploaded[0]);
          this.mediaLoaded = true;
        } catch (error) {
          console.log("error", error);
        } finally {
          this.mydocloading = false;
        }
      }
    },
    EditComp(item) {
      this.compObj = { ...item };
      this.sidebarTitle = "Edit Complaint";
      this.buttonTitle = "Update";
      this.visibility = true;

      if (this.compObj.attachment == null || this.compObj.attachment == "")
        this.mediaLoaded = false;
      else this.mediaLoaded = true;

      var elem = this.$refs["complaint"];
      elem.state = undefined;
      var elem = this.$refs["date"];
      elem.state = undefined;
    },
    async saveComp() {
      // console.log(this.compObj);
      if (this.CheckComp() == true && this.CheckCompDate() == true) {
        if (this.compObj.id == 0) {
          this.request = true;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "Complaints?db=" +
              this.$store.state.userData.db,
            body: this.compObj,
            message: "Complaint added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.loadComplaints();
            this.visibility = false;
          }
        } else {
          this.request = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "Complaints/" +
              this.compObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.compObj,
            message: "Complaint updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.loadComplaints();
            this.visibility = false;
          }
        }
      }
    },
    async DeleteComp(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Complaints/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          message: "Complaint removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadComplaints();
      }
    },
    resolve(item) {
      this.resObj = { ...item };
      this.visibility2 = true;
    },
    CheckResName() {
      var elem = this.$refs["resBy"];
      if (this.resObj.resolvedBy == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckNote() {
      var elem = this.$refs["note"];
      if (this.resObj.note == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckResDate() {
      var elem = this.$refs["resdate"];
      if (this.resObj.resolvedOn == "" || this.resObj.resolvedOn == null) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async save() {
      // console.log("ob", this.resObj);
      if (
        this.CheckNote() == true &&
        this.CheckResDate() == true &&
        this.CheckResName() == true
      ) {
        this.request = true;
        this.resObj.status = "resolved";
        var status = await this.put({
          url:
            this.$store.state.domain +
            "schoolComplaints/" +
            this.resObj.id +
            "?db=" +
            this.$store.state.userData.db,
          body: this.resObj,
          message: "Complaint resolved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;

        if (status) {
          this.loadParent();
          this.visibility2 = false;
        }
      }
    },
    async loadExamCat() {
      var obj = {
        url:
          this.$store.state.domain +
          "ExamTypes?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID,
        token: this.$store.state.userData.token,
      };
      this.examTypes = await this.get(obj);

      // console.log("exam cat", this.examTypes);
    },
    View(item) {
      // Transform subjects/marks data to match the expected format
      if (item.subjects && Array.isArray(item.subjects)) {
        // Handle old API structure with subjects array
        this.marksData = item.subjects.map((subject) => {
          // Parse percentage - remove % sign if present
          let percentage = 0;
          if (subject.percentage) {
            const percentageStr = subject.percentage
              .toString()
              .replace("%", "");
            percentage = parseFloat(percentageStr) || 0;
          }

          return {
            subject: subject.subject,
            marks: {
              attendance: item.results?.attendance || "-",
              obtained: subject.subjectObtained,
              total: subject.subjectTotal,
              meta_sub_grade: subject.grade,
              meta_subj_percenatge: percentage,
              rank: "-", // Subject rank not available in API
              status: subject.status,
            },
          };
        });
      } else if (item.marks && Array.isArray(item.marks)) {
        // Handle new API structure with marks array
        this.marksData = item.marks.map((markItem) => {
          const mark = markItem.marks || {};
          // Parse percentage - handle null or number
          let percentage = 0;
          if (
            mark.meta_subj_percenatge !== null &&
            mark.meta_subj_percenatge !== undefined
          ) {
            const percentageValue = mark.meta_subj_percenatge;
            if (typeof percentageValue === "string") {
              const percentageStr = percentageValue.toString().replace("%", "");
              percentage = parseFloat(percentageStr) || 0;
            } else {
              percentage = parseFloat(percentageValue) || 0;
            }
          }

          return {
            subject: markItem.subject,
            marks: {
              attendance: mark.attendance || item.results?.attendance || "-",
              obtained: mark.obtained || mark.originalObtained || 0,
              total: mark.total || 0,
              meta_sub_grade: mark.meta_sub_grade || mark.grade || null,
              meta_subj_percenatge: percentage,
              rank: mark.rank || "-",
              status: mark.status || null,
            },
          };
        });
      } else {
        this.marksData = [];
      }
      if (this.useMarksDetails) {
        this.prepareAssessmentModal(item);
        this.expandedAssessmentId = this.getAssessmentIdentifier(item);
        this.marksGrid = false;
      } else {
        this.closeAssessmentPanel();
        this.marksGrid = true;
      }
    },
    getAssessmentIdentifier(assessment) {
      return (
        assessment?.results?.id ??
        assessment?.examID ??
        `${assessment?.examType || "assessment"}-${
          assessment?.session || "session"
        }`
      );
    },
    prepareAssessmentModal(assessment) {
      if (!assessment) {
        this.assessmentRows = [];
        this.assessmentSubjectFilters = ["All"];
        this.activeAssessmentSubject = "All";
        this.assessmentModalTitle = "Assessment Details";
        return;
      }

      this.assessmentModalTitle = `${assessment.examType || "Assessment"}${
        assessment.session ? " • " + assessment.session : ""
      }`;

      this.assessmentRows = this.buildAssessmentRows(assessment);
      const subjects = Array.from(
        new Set(this.assessmentRows.map((row) => row.subject))
      );
      this.assessmentSubjectFilters = ["All", ...subjects];
      this.activeAssessmentSubject = "All";
    },
    buildAssessmentRows(assessment) {
      const examName = assessment.examType || "N/A";
      const examDate =
        assessment.results?.dtFrom ||
        assessment.results?.dtTo ||
        assessment.date ||
        null;
      const formattedDate = this.formatAssessmentDate(examDate);
      const subjects = this.normalizeAssessmentSubjects(assessment);
      const rows = [];

      subjects.forEach((subject) => {
        subject.chapterEntries.forEach((entry) => {
          const chapterLabel = entry.chapterLabel || "Overall";
          const topics =
            entry.topicLabels && entry.topicLabels.length
              ? ` (${entry.topicLabels.join(", ")})`
              : "";
          rows.push({
            subject: subject.name || "N/A",
            date: formattedDate,
            exam: examName,
            chapter: `${chapterLabel}${topics}`,
            score: `${entry.obtainedMarks ?? 0} / ${entry.totalMarks ?? 0}`,
            grade: subject.grade || "",
            status: subject.status || "",
          });
        });
      });

      return rows;
    },
    normalizeAssessmentSubjects(assessment) {
      const normalized = [];
      if (assessment.subjects && Array.isArray(assessment.subjects)) {
        assessment.subjects.forEach((subject) => {
          const entries =
            subject.marks &&
            Array.isArray(subject.marks) &&
            subject.marks.length
              ? subject.marks.map((mark) => {
                  const topics =
                    Array.isArray(mark.chapters) && mark.chapters.length
                      ? mark.chapters
                          .map((chapter) => chapter.topic)
                          .filter(Boolean)
                      : [];
                  return {
                    chapterLabel: mark.chapter || "Overall",
                    totalMarks:
                      mark.totalMarks ??
                      mark.total ??
                      subject.subjectTotal ??
                      0,
                    obtainedMarks:
                      mark.obtainedMarks ??
                      mark.obtained ??
                      subject.subjectObtained ??
                      0,
                    topicLabels: topics.length ? topics : null,
                  };
                })
              : [
                  {
                    chapterLabel: "Overall",
                    totalMarks: subject.subjectTotal ?? 0,
                    obtainedMarks: subject.subjectObtained ?? 0,
                    topicLabels: null,
                  },
                ];

          normalized.push({
            name: subject.subject || "N/A",
            grade: subject.grade,
            status: subject.status,
            chapterEntries: entries,
          });
        });
      } else if (assessment.marks && Array.isArray(assessment.marks)) {
        assessment.marks.forEach((markItem) => {
          const mark = markItem.marks || {};
          const entries = [];

          if (Array.isArray(mark.chapters) && mark.chapters.length) {
            mark.chapters.forEach((chapter) => {
              entries.push({
                chapterLabel:
                  chapter.topic || chapter.chapter || mark.chapter || "Chapter",
                totalMarks:
                  chapter.totalMarks || chapter.total || mark.total || 0,
                obtainedMarks:
                  chapter.obtainedMarks ||
                  chapter.obtained ||
                  mark.obtained ||
                  0,
                topicLabels:
                  Array.isArray(chapter.chapters) && chapter.chapters.length
                    ? chapter.chapters
                        .map((nested) => nested.topic)
                        .filter(Boolean)
                    : chapter.topic
                    ? [chapter.topic]
                    : null,
              });
            });
          }

          if (!entries.length) {
            entries.push({
              chapterLabel: mark.chapter || "Overall",
              totalMarks: mark.total || mark.totalMarks || 0,
              obtainedMarks: mark.obtained || mark.obtainedMarks || 0,
              topicLabels: null,
            });
          }

          normalized.push({
            name: markItem.subject || "N/A",
            grade: mark.meta_sub_grade || mark.grade || markItem.grade,
            status: mark.status || markItem.status,
            chapterEntries: entries,
          });
        });
      }
      return normalized;
    },
    formatAssessmentDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (error) {
        return "N/A";
      }
    },
    setAssessmentSubject(subject) {
      this.activeAssessmentSubject = subject;
    },
    closeAssessmentPanel() {
      this.expandedAssessmentId = null;
      this.assessmentRows = [];
      this.assessmentSubjectFilters = ["All"];
      this.activeAssessmentSubject = "All";
      this.assessmentModalTitle = "Assessment Details";
    },
    async loadResult() {
      const studentIds = Array.isArray(this.$route.params.id)
        ? this.$route.params.id.join(",")
        : this.$route.params.id;
      const encodedDb = encodeURIComponent(this.$store.state.userData.db);
      const baseParams =
        "?db=" +
        encodedDb +
        "&cID=" +
        this.userData.campusID +
        "&sID=" +
        studentIds;
      const studentUrl =
        this.$store.state.domain + "Marks/StudentResult" + baseParams;
      const marksUrl =
        this.$store.state.domain + "Marks/StudentResult" + baseParams;

      let response = null;

      const tryFetch = async (url, label) => {
        try {
          return await this.get({
            url,
            token: this.$store.state.userData.token,
          });
        } catch (error) {
          console.error(`${label} API error:`, error);
          return null;
        }
      };

      if (this.useMarksDetails) {
        response = await tryFetch(marksUrl, "Marks/StudentResult");
        if (!response) {
          response = await tryFetch(studentUrl, "Marks/StudentResult");
        }
      } else {
        response = await tryFetch(studentUrl, "Marks/StudentResult");
        if (!response) {
          response = await tryFetch(marksUrl, "Marks/StudentResult");
        }
      }

      this.assessments = this.extractAssessmentsFromResponse(response);
      this.closeAssessmentPanel();
    },
    async LoadMarkGraph() {
      var obj = {
        url:
          this.$store.state.domain +
          "Marks/AssessmentGraph?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.userData.campusID +
          "&sID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      this.marksChart = res.filter((el) => el.marks.length > 0);
      // console.log(this.marksChart);

      let labels = [];
      let datasets = [];
      this.marksChart.forEach((el, ind) => {
        if (ind <= 3) {
          labels.push(el.subject);
          let obj = {
            data: el.marks.map((elem) => elem.obtained),
            label: "",
            borderColor: $themeColors[this.color[ind]],
            lineTension: 0.5,
            pointStyle: "circle",
            backgroundColor: $themeColors[this.color[ind]],
            fill: false,
            pointRadius: 1,
            pointHoverRadius: 5,
            pointHoverBorderWidth: 5,
            pointBorderColor: "transparent",
            pointHoverBorderColor: $themeColors[this.color[ind]],
            pointHoverBackgroundColor: $themeColors[this.color[ind]],
            pointShadowOffsetX: 1,
            pointShadowOffsetY: 1,
            pointShadowBlur: 5,
            pointShadowColor: "rgba(0, 0, 0, 0.25)",
          };
          datasets.push(obj);
        }
      });
      // console.log(datasets, labels);
      this.lineChart.data.labels = labels;
      this.lineChart.data.datasets = datasets;
      this.showChart = true;
    },
    extractAssessmentsFromResponse(response) {
      if (!response) return [];

      // Handle structure: { status, data: [{ results: [...] }] }
      if (
        response.status &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        const recordWithResults = response.data.find(
          (entry) => Array.isArray(entry.results) && entry.results.length > 0
        );
        if (recordWithResults) {
          return recordWithResults.results;
        }
      }

      // Handle structure: { data: { results: [...] } }
      if (
        response.data &&
        Array.isArray(response.data.results) &&
        response.data.results.length > 0
      ) {
        return response.data.results;
      }

      // Handle direct object with results
      if (Array.isArray(response.results) && response.results.length > 0) {
        return response.results;
      }

      // Handle legacy array structure
      if (Array.isArray(response) && response.length > 0) {
        if (Array.isArray(response[0].results)) {
          return response[0].results;
        }
        return response;
      }

      return [];
    },
    async loadPtm() {
      this.ptmLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "ParentTeachersMeeting/LoadData?db=" +
          this.$store.state.userData.db +
          "&sID=" +
          this.$route.params.id,
        token: this.$store.state.userData.token,
      };
      this.ptmData = await this.get(obj);
      // console.log("ptm:", this.ptmData);
      this.ptmLoading = false;
    },
    async printPtm() {
      this.ptmPrinting = true;
      try {
        var obj = {
          url:
            this.$store.state.domain +
            "ParentTeachersMeeting/Filter?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.userData.campusID +
            "&sID=" +
            this.$route.params.id,
          token: this.$store.state.userData.token,
        };
        let res = await this.get(obj);

        if (res && res.length > 0) {
          const printContent = this.generatePtmReportHTML(res);

          const newWindow = window.open(
            "",
            "_blank",
            "width=1200,height=800,scrollbars=yes,resizable=yes"
          );

          if (newWindow) {
            newWindow.document.write(printContent);
            newWindow.document.close();

            newWindow.onload = function () {
              const printBtn = newWindow.document.getElementById("printButton");
              if (printBtn) {
                printBtn.addEventListener("click", function () {
                  newWindow.print();
                });
              }
            };
          } else {
            alert(
              "Pop-up blocked! Please allow pop-ups for this site to view the PTM report."
            );
          }
        } else {
          this.$bvToast.toast("No PTM records found to print.", {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        }
      } catch (error) {
        console.error("Error generating PTM report:", error);
      } finally {
        this.ptmPrinting = false;
      }
    },

    generatePtmReportHTML(ptmData) {
      // Generate table rows with pagination (max 50 records per page)
      const records = ptmData || [];
      const totalRecords = records.length;

      const recordsPerPage = 50;
      const totalPages = Math.ceil(totalRecords / recordsPerPage) || 1;

      let allTableRows = "";

      for (let page = 0; page < totalPages; page++) {
        const startIndex = page * recordsPerPage;
        const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
        const pageRecords = records.slice(startIndex, endIndex);

        const tableRows = pageRecords
          .map((item, index) => {
            const date = this.formatTableDate(item.date);
            const summary = item.summary || "N/A";

            return `
        <tr ${index % 2 === 0 ? 'class="even-row"' : ""}>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px; width: 8%;">${
            startIndex + index + 1
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px; width: 22%;">${date}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px; width: 70%;">${summary}</td>
        </tr>
      `;
          })
          .join("");

        // Add page break for multiple pages
        const pageBreak =
          page < totalPages - 1 ? "page-break-after: always;" : "";

        allTableRows += `
          <div style="${pageBreak}">
            ${
              page > 0
                ? `<div class="page-header"><h2>PTM Report - Page ${
                    page + 1
                  } of ${totalPages}</h2></div>`
                : ""
            }
            <table class="report-table" style="border: 1px solid #ccc !important; border-collapse: collapse !important; border-spacing: 0 !important; width: 100%;">
              ${
                page === 0
                  ? `
              <thead>
                <tr>
                  <th class="text-center" style="width: 8%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">S.NO</th>
                  <th class="text-left" style="width: 22%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Date</th>
                  <th class="text-left" style="width: 70%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Summary</th>
                </tr>
              </thead>
              `
                  : ""
              }
              <tbody>
                ${
                  tableRows ||
                  `<tr><td colspan="3" class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">No records found</td></tr>`
                }
              </tbody>
            </table>
          </div>
        `;
      }

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>PTM Report - ${this.userData.name || "Student"}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.4;
              color: #333;
              background-color: #fff !important;
              padding: 15px;
              font-size: 12px;
              margin: 0;
              width: 100%;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 15px;
              border-radius: 8px;
              color: #212529;
              background: linear-gradient(135deg, #f8f9fa, #e9ecef);
              border: 2px solid #dee2e6;
            }
            
            .print-header h1 {
              margin: 0 0 10px 0;
              color: #212529;
              font-size: 24px;
              font-weight: bold;
            }
            
            .student-info {
              margin-bottom: 20px;
              padding: 20px;
              background: linear-gradient(135deg, #f8f9fa, #e9ecef);
              border-radius: 12px;
              border: 2px solid #dee2e6;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            .student-details {
              width: 100%;
            }
            
            .student-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 2px solid #dee2e6;
            }
            
            .student-name-section {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            
            .student-image {
              flex-shrink: 0;
            }
            
            .student-name-info h3 {
              margin: 0;
              color: #495057;
              font-size: 22px;
              font-weight: bold;
            }
            .student-name-info h4 {
              margin: 0;
              color: #495057;
              font-size: 18px;
              font-weight: bold;
            }
            
            .student-badges {
              display: flex;
              gap: 10px;
              align-items: center;
            }
            
            .gr-badge {
              background: linear-gradient(135deg, #667eea, #764ba2);
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
            }
            
            .status-badge {
              background: linear-gradient(135deg, #28a745, #20c997);
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
            }
            
            .student-info-compact {
              margin-top: 10px;
            }
            
            .info-badges {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 8px;
            }
            
            .info-badges-single {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              margin-bottom: 8px;
              align-items: center;
            }
            
            .info-badge {
              background: white;
              color: black;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 500;
              border: 1px solid #ccc;
              white-space: nowrap;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            @media (max-width: 768px) {
              .info-badges {
                flex-direction: column;
                gap: 6px;
              }
              
              .info-badge {
                text-align: center;
                padding: 6px 8px;
              }
            }
            
            .info-item {
              padding: 10px;
              background: white;
              border-radius: 8px;
              border: 1px solid #e9ecef;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .info-item strong {
              color: #495057;
              font-weight: 600;
              font-size: 13px;
            }
            
            .page-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            
            .page-header h2 {
              margin: 0;
              color: #667eea;
              font-size: 18px;
              font-weight: bold;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              background: white;
              border: 1px solid #ccc !important;
              border-spacing: 0 !important;
            }
            
            .report-table th {
              background-color: #e9ecef !important;
              color: #212529 !important;
              padding: 6px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #ccc !important;
            }
            
            .report-table td {
              padding: 6px 8px;
              border: 1px solid #ccc !important;
              font-size: 12px;
              vertical-align: middle;
            }
            
            .report-table tr.even-row {
              background-color: #f8f9fa;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #6c757d;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 13px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                width: 100% !important;
                height: 100% !important;
                font-size: 11px !important;
              }
              
              .print-controls {
                display: none !important;
              }
              
              .print-header {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .student-info {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .info-badge {
                background: white !important;
                color: black !important;
                border: 1px solid #ccc !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .report-table th {
                background-color: #e9ecef !important;
                color: #212529 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              @page {
                size: A4 landscape;
                margin: 0.5in;
                background: white !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="background: white !important; width: 100%; min-height: 100vh; margin: 0; padding: 0;">
            <!-- Print Controls -->
            <div class="print-controls">
              <button id="printButton" class="print-btn">
                🖨️ Print Report
              </button>
            </div>
            
            <!-- Header -->
            <div class="print-header">
              <h1>PTM Report</h1>
              <p>Generated on ${new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}</p>
            </div>
            
            <!-- Student Information -->
            <div class="student-info">
              <div class="student-details">
                <div class="student-header">
                  <div class="student-name-section">
                    <div class="student-image">
                      <img src="${
                        this.getStudentPictureForPrint() ||
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNlOWVjZWYiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNmM3NTdkIj4KPHBhdGggZD0iTTEyIDEyYzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMCAyYy-yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYzAtMi42Ni01LjMzLTQtOC00eiIvPgo8L3N2Zz4KPC9zdmc+Cg=="
                      }" 
                           alt="Student Photo" 
                           style="width: 80px; height: 80px; border-radius: 50%; border: 3px solid #dee2e6; object-fit: cover;" 
                           onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNlOWVjZWYiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNmM3NTdkIj4KPHBhdGggZD0iTTEyIDEyYzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMCAyYy-yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYzAtMi42Ni01LjMzLTQtOC00eiIvPgo8L3N2Zz4KPC9zdmc+Cg=='" />
                    </div>
                    <div class="student-name-info">
                      <h3>${this.userData.name || "Student Name"}</h3>
                      <h4>${this.userData.father_name || "Father Name"}</h4>
                      <div class="student-info-compact">
                        <div class="info-badges-single">
                          <span class="info-badge">GR - ${
                            this.userData.grNo || "N/A"
                          }</span>
                          <span class="info-badge">F# - ${
                            this.userData.family_code || "N/A"
                          }</span>
                          <span class="info-badge">RFID: ${
                            this.userData.rfid_number || "N/A"
                          }</span>
                          <span class="info-badge">ID: ${
                            this.userData.id || "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.date_of_admission
                              ? new Date(
                                  this.userData.date_of_admission
                                ).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.contact_father || "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.cls_current?.name ||
                            this.userData.class_admitted ||
                            this.userData.class ||
                            "N/A"
                          }</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Main Table -->
            ${allTableRows}
            
          </div>
        </body>
        </html>
      `;
    },

    async loadTransferHistory() {
      const localID =
        this.userData?.localID || this.userData?.id || this.$route.params.id;
      if (!localID) {
        this.transferHistoryLogs = [];
        return;
      }

      this.transferHistoryLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "StudentStaffDataLog/GetHistory?db=" +
            this.$store.state.userData.db +
            "&type=student&localID=" +
            localID,
          token: this.$store.state.userData.token,
        };

        const response = await this.get(obj);
        let rows = [];

        if (response?.status === "success" && Array.isArray(response.data)) {
          rows = response.data;
        } else if (Array.isArray(response?.data)) {
          rows = response.data;
        } else if (Array.isArray(response)) {
          rows = response;
        }

        this.transferHistoryLogs = rows.sort(
          (a, b) => new Date(b.updatedOn) - new Date(a.updatedOn)
        );
      } catch (error) {
        console.error("Error loading transfer history:", error);
        this.transferHistoryLogs = [];
      } finally {
        this.transferHistoryLoading = false;
      }
    },

    formatLogCategory(category) {
      if (!category) return this.$t("Record Update");
      const normalized = String(category).toLowerCase().trim();
      if (normalized === "transfer") return this.$t("Transfer");
      if (normalized === "class change") return this.$t("Class Change");
      return category;
    },

    getHistoryIcon(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return "BookOpenIcon";
      return "RefreshCwIcon";
    },

    getHistoryTitleIcon(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return "BookOpenIcon";
      return "HomeIcon";
    },

    getHistoryCardClass(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return "history-card--class";
      if (normalized === "transfer") return "history-card--transfer";
      return "history-card--default";
    },

    getHistoryRoute(details) {
      if (!details) return null;
      const match = String(details).match(/\bfrom\s+(.+?)\s+to\s+(.+)$/i);
      if (!match) return null;
      return {
        from: match[1].trim(),
        to: match[2].trim(),
      };
    },

    getHistoryBadgeVariant(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return "light-info";
      return "light-primary";
    },

    getHistoryPointClass(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return "timeline-point--class";
      return "";
    },

    getHistoryNoteLabel(category) {
      const normalized = String(category || "")
        .toLowerCase()
        .trim();
      if (normalized === "class change") return this.$t("Class Change Notes");
      return this.$t("Transfer Notes");
    },

    getParsedHistoryLog(log) {
      if (!log || !log.details) {
        return { changes: [], text: "" };
      }

      try {
        const parsed =
          typeof log.details === "string"
            ? JSON.parse(log.details)
            : log.details;

        const previous = parsed.previous || {};
        const current = parsed.new || {};

        if (typeof previous !== "object" || typeof current !== "object") {
          return {
            changes: [
              {
                label: "",
                from: previous || "—",
                to: current || "—",
                isChanged: previous !== current,
              },
            ],
            text: "",
          };
        }

        const labels = {
          session: "Semester",
          class: "Group",
          section: "Level",
          agecategory: "Age Category",
          empoweLevel: "Empower Level",
        };

        const ignoredKeys = [
          "sessionid",
          "classid",
          "sectionid",
          "campusid",
          "id",
        ];

        const keys = Array.from(
          new Set([...Object.keys(previous), ...Object.keys(current)])
        ).filter((key) => !ignoredKeys.includes(key.toLowerCase()));

        const changes = keys.map((key) => {
          const from = previous[key] || "—";
          const to = current[key] || "—";
          return {
            label: labels[key] || key,
            from,
            to,
            isChanged: from !== to,
          };
        });

        return { changes, text: "" };
      } catch (e) {
        return { changes: [], text: log.details };
      }
    },

    getHistoryThemeClass(cat, type) {
      const c = (cat || "").toLowerCase();
      let theme = "primary";

      if (c.includes("transfer") || c.includes("campus")) {
        theme = "transfer";
      } else if (c.includes("class") || c.includes("grade")) {
        theme = "class";
      } else if (c.includes("section")) {
        theme = "section";
      } else if (c.includes("session") || c.includes("year")) {
        theme = "session";
      } else if (c.includes("status")) {
        theme = "status";
      } else if (c.includes("promote") || c.includes("promotion")) {
        theme = "promote";
      }

      if (type === "point") return `history-timeline-point--${theme}`;
      if (type === "chip") return `history-category-chip--${theme}`;
      if (type === "pill") return `history-pill--next-${theme}`;
      return "";
    },

    formatHistoryDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) {
        return String(dateStr).split("T")[0];
      }
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    formatTableDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    getAttStatusClass(status) {
      const code = String(status || "").toLowerCase();
      if (code === "p") return "att-status-pill--present";
      if (code === "a") return "att-status-pill--absent";
      if (code === "h") return "att-status-pill--holiday";
      return "att-status-pill--other";
    },

    // Medical Records Methods
    resolveStudentClassSection() {
      return {
        classID:
          this.userData.cls_current?.id ||
          this.userData.class_current ||
          this.userData.class_admitted ||
          0,
        sectionID:
          this.userData.section_current ||
          this.userData.section_admitted ||
          this.userData.secID ||
          0,
      };
    },
    async fetchTeachersBySubject(subjectID) {
      if (!subjectID) return [];

      const teachers = await this.get({
        url:
          this.$store.state.domain +
          "Subjects/LoadTeacherBySubjects?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&subjectID=" +
          subjectID,
        token: this.$store.state.userData.token,
      });

      return Array.isArray(teachers) ? teachers : [];
    },
    resolveTeacherName(enrollment, teachers) {
      return (
        enrollment.name ||
        enrollment.teacherName ||
        enrollment.staffName ||
        enrollment.meta_teacherName ||
        (teachers || []).find(
          (teacher) => Number(teacher.id) === Number(enrollment.tID)
        )?.name ||
        "-"
      );
    },
    resolveTeacherShare(enrollment, amount, teachers) {
      const storedShare = Number(enrollment.teacherShareAmount);
      if (!Number.isNaN(storedShare) && storedShare > 0) {
        return storedShare;
      }

      const teacher = (teachers || []).find(
        (row) => Number(row.id) === Number(enrollment.tID)
      );
      if (teacher?.sharePerc && amount > 0) {
        return parseInt(amount * (teacher.sharePerc / 100));
      }

      return 0;
    },
    async buildSubjectFeeRows(feeStudent, enrollments) {
      const feeSubjects = feeStudent?.subjects || [];
      const teacherCache = new Map();
      const rows = [];

      const getTeachers = async (subID) => {
        if (!teacherCache.has(subID)) {
          teacherCache.set(subID, await this.fetchTeachersBySubject(subID));
        }
        return teacherCache.get(subID);
      };

      const activeEnrollments = (enrollments || []).filter(
        (item) => item.subID && !item.isExcluded
      );

      if (activeEnrollments.length) {
        for (const enrollment of activeEnrollments) {
          const feeSubject = feeSubjects.find(
            (subject) => Number(subject.subjectID) === Number(enrollment.subID)
          );
          const amount = Number(
            feeSubject?.subjectFee ?? enrollment.subjectFee ?? 0
          );
          const teachers = enrollment.tID
            ? await getTeachers(enrollment.subID)
            : [];

          rows.push({
            subjectName: enrollment.subName || feeSubject?.subjectName || "-",
            teacherName: this.resolveTeacherName(enrollment, teachers),
            subjectFee: amount,
            teacherShareAmount: this.resolveTeacherShare(
              enrollment,
              amount,
              teachers
            ),
          });
        }
      } else {
        for (const feeSubject of feeSubjects.filter(
          (subject) => !subject.isExcluded
        )) {
          const amount = Number(feeSubject.subjectFee) || 0;
          rows.push({
            subjectName: feeSubject.subjectName || "-",
            teacherName: feeSubject.teacherName || feeSubject.staffName || "-",
            subjectFee: amount,
            teacherShareAmount:
              Number(feeSubject.teacherShareAmount || feeSubject.shareAmount) ||
              0,
          });
        }
      }

      return rows;
    },
    async loadSubjectFees() {
      if (
        !this.isSkhDomain ||
        this.subjectFeesLoaded ||
        this.subjectFeesLoading
      ) {
        return;
      }

      const { classID, sectionID } = this.resolveStudentClassSection();
      this.subjectFeesLoading = true;

      try {
        const [feeResponse, studentData] = await Promise.all([
          axios.get(
            `${this.$store.state.domain}StudentSubjectsEnrollnment/GetSubjectFeeReport`,
            {
              params: {
                db: this.$store.state.userData.db,
                cID:
                  this.userData.campusID || this.$store.state.userData.cId || 0,
                classID: classID || 0,
                sectionID: sectionID || 0,
                sessionID:
                  this.userData.sessionID || this.$store.state.sessionID || 0,
                sID: this.userData.id,
              },
              headers: {
                Authorization: `Bearer ${this.$store.state.userData.token}`,
                accept: "application/json",
              },
            }
          ),
          this.get({
            url:
              this.$store.state.domain +
              "Students/LoadStudentData?db=" +
              this.$store.state.userData.db +
              "&sID=" +
              this.userData.id,
            token: this.$store.state.userData.token,
          }),
        ]);

        let feeStudent = null;
        if (feeResponse.data && feeResponse.data.status === "success") {
          const students = feeResponse.data.data || [];
          feeStudent =
            students.find(
              (row) => Number(row.sID) === Number(this.userData.id)
            ) || students[0];
        }

        const enrollments = studentData?.subjectsEnrollnment || [];
        this.subjectFees = await this.buildSubjectFeeRows(
          feeStudent,
          enrollments
        );
        this.subjectTotalFee =
          Number(feeStudent?.totalSubjectFee) ||
          this.subjectFees.reduce((sum, row) => sum + row.subjectFee, 0);
        this.subjectFeesLoaded = true;
      } catch (error) {
        console.error("Error loading subject fee report:", error);
        this.subjectFees = [];
        this.subjectTotalFee = 0;
      } finally {
        this.subjectFeesLoading = false;
      }
    },
    async loadSiblings() {
      if (this.siblingsLoaded) return;

      this.siblingsLoading = true;
      try {
        const studentId = this.$route.params.id || this.userData.id;
        const response = await this.get({
          url:
            this.$store.state.domain +
            "Students/GetSiblings?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            studentId,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload =
          response && response.status === "success"
            ? response
            : response && response.data && response.data.status === "success"
            ? response.data
            : response;

        const rows =
          payload && Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload)
            ? payload
            : [];

        this.siblingsList = [...rows].sort((a, b) => {
          const orderA = Number(a.hierarchyOrder) || 0;
          const orderB = Number(b.hierarchyOrder) || 0;
          if (orderA && orderB && orderA !== orderB) return orderA - orderB;
          const dobA = a.date_of_birth
            ? new Date(a.date_of_birth).getTime()
            : 0;
          const dobB = b.date_of_birth
            ? new Date(b.date_of_birth).getTime()
            : 0;
          return dobA - dobB;
        });

        this.siblingsFamilyCode =
          (payload && (payload.familyCode || payload.family_code)) ||
          this.userData.familyCode ||
          this.userData.family_code ||
          null;
        this.siblingsLoaded = true;
      } catch (error) {
        console.error("Error loading siblings:", error);
        this.siblingsList = [];
        this.siblingsFamilyCode =
          this.userData.familyCode || this.userData.family_code || null;
      } finally {
        this.siblingsLoading = false;
      }
    },

    getSiblingPicture(sibling) {
      return this.getPicturePreview(
        sibling?.picture || sibling?.avatar || sibling?.photo || ""
      );
    },

    formatSiblingDob(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },

    getSiblingStatusVariant(status) {
      const value = String(status || "").toLowerCase();
      if (value === "present") return "light-success";
      if (value === "left" || value === "struck off") return "light-danger";
      if (value === "registered") return "light-info";
      if (value === "suspend" || value === "suspended") return "light-warning";
      if (value === "alumni") return "light-secondary";
      return "light-secondary";
    },

    getSiblingStatusClass(status) {
      const value = String(status || "").toLowerCase();
      if (value === "present") return "is-present";
      if (value === "left" || value === "struck off") return "is-left";
      if (value === "registered") return "is-registered";
      if (value === "suspend" || value === "suspended") return "is-suspend";
      if (value === "alumni") return "is-alumni";
      return "is-default";
    },

    openSiblingProfile(sibling) {
      if (!sibling || !sibling.id) return;
      const routeData = this.$router.resolve({
        name: "apps-users-view",
        params: {
          id: sibling.id,
          item: "sibling",
        },
      });
      window.open(routeData.href, "_blank");
    },

    async loadMedicalRecords() {
      this.medicalLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "MedicalVisits/LoadByStudent?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            this.$route.params.id,
          token: this.$store.state.userData.token,
        };

        console.log("Loading medical records with URL:", obj.url);
        const response = await this.get(obj);
        console.log("Medical records response:", response);

        if (
          response &&
          response.status === "success" &&
          Array.isArray(response.data)
        ) {
          this.medicalRecords = response.data;
          console.log(
            "Medical records loaded for student:",
            this.medicalRecords.length
          );
        } else if (response && Array.isArray(response.data)) {
          this.medicalRecords = response.data;
          console.log(
            "Medical records loaded for student:",
            this.medicalRecords.length
          );
        } else if (response && Array.isArray(response)) {
          this.medicalRecords = response;
          console.log(
            "Medical records loaded for student (direct array):",
            this.medicalRecords.length
          );
        } else {
          this.medicalRecords = [];
          console.log("No medical records data received");
        }
      } catch (error) {
        console.error("Error loading medical records:", error);
        this.medicalRecords = [];
      } finally {
        this.medicalLoading = false;
      }
    },

    formatMedicalDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    openImageModal(imageUrl) {
      // Open image in a new window/tab for better viewing
      window.open(
        imageUrl,
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes"
      );
    },

    // Print Medical Report
    printMedicalReport() {
      console.log("Generating medical report print...");

      // Create the HTML content for the print view
      const printContent = this.generateMedicalReportHTML();

      // Open new window/tab
      const newWindow = window.open(
        "",
        "_blank",
        "width=1200,height=800,scrollbars=yes,resizable=yes"
      );

      if (newWindow) {
        newWindow.document.write(printContent);
        newWindow.document.close();

        // Add print functionality after the content is loaded
        newWindow.onload = function () {
          // Add print button functionality
          const printBtn = newWindow.document.getElementById("printButton");
          if (printBtn) {
            printBtn.addEventListener("click", function () {
              newWindow.print();
            });
          }
        };
      } else {
        alert(
          "Pop-up blocked! Please allow pop-ups for this site to view the medical report."
        );
      }
    },

    generateMedicalReportHTML() {
      // Generate table rows with pagination (max 50 records per page)
      const totalRecords = this.medicalRecords.length;
      console.log("Generating medical report HTML with records:", totalRecords);

      const recordsPerPage = 50;
      const totalPages = Math.ceil(totalRecords / recordsPerPage);

      let allTableRows = "";

      for (let page = 0; page < totalPages; page++) {
        const startIndex = page * recordsPerPage;
        const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
        const pageRecords = this.medicalRecords.slice(startIndex, endIndex);

        const tableRows = pageRecords
          .map((item, index) => {
            const reportedBy = item.reportedBy || "N/A";
            const symptoms = item.symptoms || "N/A";
            const actionTaken = item.actionTakenAtSchool || "N/A";
            const medication = item.medicationGiven || "N/A";
            const observation = item.observation || "N/A";
            const parentAction = item.parentActionRequired || "N/A";
            const remarks = item.remarks || "N/A";
            const date = this.formatMedicalDate(item.date);

            return `
        <tr ${index % 2 === 0 ? 'class="even-row"' : ""}>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${
            startIndex + index + 1
          }</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${date}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${reportedBy}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${symptoms}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${actionTaken}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${medication}</td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${observation}</td>
          <td class="text-center" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">
            <span class="badge ${
              parentAction === "Yes" ? "badge-danger" : "badge-success"
            }">${parentAction}</span>
          </td>
          <td class="text-left" style="border: 1px solid #ccc !important; padding: 6px 8px; font-size: 12px;">${remarks}</td>
        </tr>
      `;
          })
          .join("");

        // Add page break for multiple pages
        const pageBreak =
          page < totalPages - 1 ? "page-break-after: always;" : "";

        allTableRows += `
          <div style="${pageBreak}">
            ${
              page > 0
                ? `<div class="page-header"><h2>Medical Report - Page ${
                    page + 1
                  } of ${totalPages}</h2></div>`
                : ""
            }
            <table class="report-table" style="border: 1px solid #ccc !important; border-collapse: collapse !important; border-spacing: 0 !important;">
              ${
                page === 0
                  ? `
              <thead>
                <tr>
                  <th class="text-center" style="width: 5%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">S.NO</th>
                  <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Date</th>
                  <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Reported By</th>
                  <th class="text-left" style="width: 15%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Symptoms</th>
                  <th class="text-left" style="width: 15%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Action Taken</th>
                  <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Medication</th>
                  <th class="text-left" style="width: 12%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Observation</th>
                  <th class="text-center" style="width: 10%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Parent Action</th>
                  <th class="text-left" style="width: 9%; border: 1px solid #ccc !important; background-color: #e9ecef !important; color: #212529 !important; padding: 6px 8px; font-size: 12px;">Remarks</th>
                </tr>
              </thead>
              `
                  : ""
              }
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `;
      }

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Medical Report - ${this.userData.name || "Student"}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.4;
              color: #333;
              background-color: #fff !important;
              padding: 15px;
              font-size: 12px;
              margin: 0;
              width: 100%;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 15px;
              border-radius: 8px;
              color: #212529;
              background: linear-gradient(135deg, #f8f9fa, #e9ecef);
              border: 2px solid #dee2e6;
            }
            
            .print-header h1 {
              margin: 0 0 10px 0;
              color: #212529;
              font-size: 24px;
              font-weight: bold;
            }
            
            .student-info {
              margin-bottom: 20px;
              padding: 20px;
              background: linear-gradient(135deg, #f8f9fa, #e9ecef);
              border-radius: 12px;
              border: 2px solid #dee2e6;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            .student-details {
              width: 100%;
            }
            
            .student-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 15px;
              border-bottom: 2px solid #dee2e6;
            }
            
            .student-name-section {
              display: flex;
              align-items: center;
              gap: 15px;
            }
            
            .student-image {
              flex-shrink: 0;
            }
            
            .student-name-info h3 {
              margin: 0;
              color: #495057;
              font-size: 22px;
              font-weight: bold;
            }
            
            .student-badges {
              display: flex;
              gap: 10px;
              align-items: center;
            }
            
            .gr-badge {
              background: linear-gradient(135deg, #667eea, #764ba2);
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
            }
            
            .status-badge {
              background: linear-gradient(135deg, #28a745, #20c997);
              color: white;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
            }
            
            .student-info-compact {
              margin-top: 15px;
            }
            
            .info-badges {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 8px;
            }
            
            .info-badges-single {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              margin-bottom: 8px;
              align-items: center;
            }
            
            .info-badge {
              background: white;
              color: black;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 500;
              border: 1px solid #ccc;
              white-space: nowrap;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .info-badges-single {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              margin-bottom: 8px;
              align-items: center;
            }
            
            @media (max-width: 768px) {
              .info-badges {
                flex-direction: column;
                gap: 6px;
              }
              
              .info-badge {
                text-align: center;
                padding: 6px 8px;
              }
            }
            
            .info-item {
              padding: 10px;
              background: white;
              border-radius: 8px;
              border: 1px solid #e9ecef;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .info-item strong {
              color: #495057;
              font-weight: 600;
              font-size: 13px;
            }
            
            .page-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            
            .page-header h2 {
              margin: 0;
              color: #667eea;
              font-size: 18px;
              font-weight: bold;
            }
            
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              background: white;
              border: 1px solid #ccc !important;
              border-spacing: 0 !important;
            }
            
            .report-table th {
              background-color: #e9ecef !important;
              color: #212529 !important;
              padding: 6px 8px;
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #ccc !important;
            }
            
            .report-table td {
              padding: 6px 8px;
              border: 1px solid #ccc !important;
              font-size: 12px;
              vertical-align: middle;
            }
            
            .report-table tr.even-row {
              background-color: #f8f9fa;
            }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .badge {
              padding: 4px 6px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 600;
            }
            
            .badge-success {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .badge-danger {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #6c757d;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 13px;
              font-weight: bold;
              box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              @page {
                margin: 0.5in !important;
                size: A4 !important;
              }
              
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                width: 100% !important;
                height: 100% !important;
                font-size: 11px !important;
              }
              
              .print-controls {
                display: none !important;
              }
              
              .print-header {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .student-info {
                background: #f8f9fa !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
                  .info-badge {
                    background: white !important;
                    color: black !important;
                    border: 1px solid #ccc !important;
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                  }
              
              .report-table th {
                background-color: #e9ecef !important;
                color: #212529 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-success {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .badge-danger {
                background-color: #f8f9fa !important;
                color: #495057 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              @page {
                size: A4 landscape;
                margin: 0.5in;
                background: white !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="background: white !important; width: 100%; min-height: 100vh; margin: 0; padding: 0;">
            <!-- Print Controls -->
            <div class="print-controls">
              <button id="printButton" class="print-btn">
                🖨️ Print Report
              </button>
            </div>
            
            <!-- Header -->
            <div class="print-header">
              <h1>Medical Report</h1>
              <p>Generated on ${new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}</p>
            </div>
            
            <!-- Student Information -->
            <div class="student-info">
              <div class="student-details">
                <div class="student-header">
                  <div class="student-name-section">
                    <div class="student-image">
                      <img src="${
                        this.getStudentPictureForPrint() ||
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNlOWVjZWYiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNmM3NTdkIj4KPHBhdGggZD0iTTEyIDEyYzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYzAtMi42Ni01LjMzLTQtOC00eiIvPgo8L3N2Zz4KPC9zdmc+Cg=="
                      }" 
                           alt="Student Photo" 
                           style="width: 80px; height: 80px; border-radius: 50%; border: 3px solid #dee2e6; object-fit: cover;" 
                           onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiIGZpbGw9IiNlOWVjZWYiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNmM3NTdkIj4KPHBhdGggZD0iTTEyIDEyYzIuMjEgMCA0LTEuNzkgNC00cy0xLjc5LTQtNC00LTQgMS43OS00IDQgMS43OSA0IDQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYzAtMi42Ni01LjMzLTQtOC00eiIvPgo8L3N2Zz4KPC9zdmc+Cg=='" />
                    </div>
                    <div class="student-name-info">
                      <h3>${this.userData.name || "Student Name"}</h3>
                      <div class="student-info-compact">
                        <div class="info-badges-single">
                          <span class="info-badge">GR - ${
                            this.userData.grNo || "N/A"
                          }</span>
                          <span class="info-badge">F# - ${
                            this.userData.family_code || "N/A"
                          }</span>
                          <span class="info-badge">RFID: ${
                            this.userData.rfid_number || "N/A"
                          }</span>
                          <span class="info-badge">ID: ${
                            this.userData.id || "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.date_of_admission
                              ? new Date(
                                  this.userData.date_of_admission
                                ).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.father_name || "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.contact_father || "N/A"
                          }</span>
                          <span class="info-badge">${
                            this.userData.cls_current?.name ||
                            this.userData.class_admitted ||
                            this.userData.class ||
                            "N/A"
                          } - ${
        this.userData.gender === "Male"
          ? "Boys"
          : this.userData.gender === "Female"
          ? "Girls"
          : "Mixed"
      }</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Main Table -->
            ${allTableRows}
            
          </div>
        </body>
        </html>
      `;
    },

    open() {
      this.addPtm = true;
      this.ptmObj = {
        id: 0,
        campusID: this.userData.campusID,
        sID: this.$route.params.id,
        date: "",
        summary: "",
      };
      var elem = this.$refs["summary"];
      elem.state = undefined;
      var elem = this.$refs["date"];
      elem.state = undefined;
    },
    Edit(item) {
      this.ptmObj = { ...item };
      this.addPtm = true;
      var elem = this.$refs["summary"];
      elem.state = undefined;
      var elem = this.$refs["date"];
      elem.state = undefined;
    },
    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "ParentTeachersMeeting/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          body: null,
          message: "Summary removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.loadPtm();
      }
    },
    CheckSummary() {
      var elem = this.$refs["summary"];
      if (this.ptmObj.summary === "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckDate() {
      var elem = this.$refs["date"];
      if (this.ptmObj.date === "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async Add() {
      this.CheckDate();
      this.CheckSummary();
      if (this.CheckDate() == true && this.CheckSummary() == true) {
        if (this.ptmObj.id == 0) {
          this.request = false;
          var status = await this.post({
            url:
              this.$store.state.domain +
              "ParentTeachersMeeting?db=" +
              this.$store.state.userData.db,
            body: this.ptmObj,
            message: "Summary added successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.loadPtm();
            this.addPtm = false;
          }
        } else {
          this.request = true;
          var status = await this.put({
            url:
              this.$store.state.domain +
              "ParentTeachersMeeting/" +
              this.ptmObj.id +
              "?db=" +
              this.$store.state.userData.db,
            body: this.ptmObj,
            message: "Summary updated successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.loadPtm();
            this.addPtm = false;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
.file-type {
  background-color: #e62e2d;
  font-family: "Helvetica", sans-serif;
  color: white;
  border-radius: 10px;
  margin-right: 15px;
  height: 50px;
  width: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.assessment-inline-card .assessment-filter-group {
  gap: 0.35rem;
}

.assessment-inline-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background-color: #fff;
}

.assessment-filter-btn {
  min-width: 70px;
  font-size: 0.75rem;
  text-transform: capitalize;
}

.tab-label {
  font-size: 0.875rem;
  font-weight: 600;
}

.tab-content-wrapper {
  padding: 0.15rem 0 0;
}

.tab-toolbar {
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.tab-panel-body {
  border: 1px solid #eef0f4 !important;
  border-radius: 14px !important;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04) !important;
  background: #ffffff !important;
  padding: 1.15rem !important;
}

.animation-fade {
  animation: planTabFadeIn 0.35s ease-out;
}

@keyframes planTabFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-panel {
  border: 1px solid #eef0f4;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  background: #ffffff;
}

.filter-btn-group {
  gap: 0.4rem;
}

.filter-period-btn {
  border-radius: 8px !important;
  font-weight: 600;
  font-size: 0.82rem;
  padding: 0.4rem 0.85rem !important;
}

.tab-data-table {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: #ffffff;

  ::v-deep .b-table-sticky-header {
    max-height: 420px;
    margin-bottom: 0;
  }

  ::v-deep .b-table-sticky-header > .table {
    margin-bottom: 0;
  }

  ::v-deep thead th {
    background: linear-gradient(180deg, #f8f6ff 0%, #f3f0ff 100%) !important;
    color: #5b21b6 !important;
    font-weight: 700 !important;
    font-size: 0.7rem !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border-bottom: 1px solid #e0d8ff !important;
    border-top: none !important;
    padding: 0.8rem 1rem !important;
    vertical-align: middle;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  ::v-deep tbody td {
    padding: 0.75rem 1rem !important;
    border-top: 1px solid #f3f4f6 !important;
    font-size: 0.88rem;
    color: #374151;
    vertical-align: middle;
  }

  ::v-deep tbody tr:hover {
    background: #faf9ff !important;
  }
}

.table-date-chip {
  display: inline-block;
  padding: 0.22rem 0.6rem;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
}

.table-time-text {
  font-size: 0.86rem;
  font-weight: 500;
  color: #4b5563;
}

.table-text-cell {
  display: block;
  max-width: 360px;
  line-height: 1.45;
  color: #374151;
}

.att-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.22rem 0.55rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
}

.att-status-pill--present {
  background: #dcfce7;
  color: #166534;
}

.att-status-pill--absent {
  background: #fee2e2;
  color: #991b1b;
}

.att-status-pill--holiday {
  background: #f3f4f6;
  color: #4b5563;
}

.att-status-pill--other {
  background: #eff6ff;
  color: #1d4ed8;
}

.transfer-history-shell {
  padding: 0.35rem 0.15rem 0.35rem 0.35rem;
  overflow-x: visible;
}

.transfer-history-shell.scrollable-tab-content {
  max-height: 65vh;
}

.transfer-history-timeline.vuexy-timeline {
  padding-left: 1.5rem;
  padding-right: 0.15rem;
  overflow: visible;
}

.transfer-history-timeline .timeline-item {
  padding-bottom: 1.75rem;
  padding-left: 2.35rem;
  border-left-color: #e0d8ff;
}

.transfer-history-timeline .timeline-item:last-child {
  padding-bottom: 0;
  border-left-color: transparent;
}

.transfer-history-timeline .timeline-point {
  width: 36px;
  height: 36px;
  left: 0;
  top: 4px;
  transform: translateX(-50%);
}

.transfer-history-timeline .timeline-point.timeline-point--class {
  border-color: #06b6d4;
  color: #06b6d4;
  box-shadow: 0 4px 8px rgba(6, 182, 212, 0.18);
}

.transfer-history-timeline .timeline-event.history-card {
  display: block;
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #eef0f4;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);

  &:hover {
    transform: none;
    border-color: #e0d8ff;
    box-shadow: 0 4px 16px rgba(124, 93, 250, 0.1);
  }
}

.transfer-history-timeline .history-card--transfer {
  border-left: 3px solid #7c5dfa;
}

.transfer-history-timeline .history-card--class {
  border-left: 3px solid #06b6d4;
}

.transfer-history-timeline .history-card--default {
  border-left: 3px solid #9ca3af;
}

/* --- Siblings hierarchy --- */
.siblings-panel {
  min-height: 300px;
  overflow: hidden;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.siblings-shell {
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e8e8ed;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.2rem 1.2rem;
}

.siblings-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid #f1f1f5;
}

.siblings-header-left {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
}

.siblings-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #7367f0;
  background: #f3f0ff;
  border: 1px solid #e4deff;
  flex-shrink: 0;
}

.siblings-title {
  font-weight: 700;
  color: #2c2c34;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
}

.siblings-subtitle {
  margin-top: 0.12rem;
  color: #8f8f9d;
  font-size: 0.8rem;
}

.siblings-header-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.siblings-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
}

.siblings-pill--family {
  color: #5e5873;
  background: #f5f5f8;
  border: 1px solid #ebebef;
}

.siblings-pill--count {
  color: #0f766e;
  background: #f0fdf8;
  border: 1px solid #d1fae5;
}

.siblings-state {
  padding: 2.25rem 1rem;
}

.siblings-empty-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f8;
  color: #8f8f9d;
}

.siblings-tree-wrap {
  max-height: 58vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.2rem;
  scrollbar-width: thin;
  scrollbar-color: #d4d4dc #f7f7fa;
}

.siblings-tree-wrap::-webkit-scrollbar {
  width: 5px;
}

.siblings-tree-wrap::-webkit-scrollbar-thumb {
  background: #d4d4dc;
  border-radius: 999px;
}

.siblings-tree {
  position: relative;
  padding-left: 0.15rem;
}

.siblings-tree-root {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
  position: relative;
  z-index: 1;
}

.siblings-tree-root-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #7367f0;
  border: 3px solid #ebe6ff;
  box-shadow: 0 0 0 1px #d8d0ff;
  margin-left: 9px;
  flex-shrink: 0;
}

.siblings-tree-root-card {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.8rem;
  border-radius: 10px;
  background: #f8f7ff;
  border: 1px solid #e4deff;
  color: #5b4cc4;
  font-size: 0.84rem;
  font-weight: 600;
}

.siblings-tree-node {
  display: flex;
  align-items: stretch;
  gap: 0;
  position: relative;
  cursor: pointer;
  outline: none;
}

.siblings-branch {
  position: relative;
  width: 32px;
  flex-shrink: 0;
}

.siblings-branch-stem {
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #d8d0ff;
}

.siblings-tree-node:last-child .siblings-branch-stem {
  bottom: calc(100% - 28px);
}

.siblings-branch-elbow {
  position: absolute;
  left: 15px;
  top: 26px;
  width: 16px;
  height: 2px;
  background: #d8d0ff;
}

.siblings-branch-dot {
  position: absolute;
  left: 9px;
  top: 20px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--sibling-accent, #7367f0);
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.2);
  z-index: 1;
}

.siblings-tone-0 {
  --sibling-accent: #7367f0;
}
.siblings-tone-1 {
  --sibling-accent: #06b6d4;
}
.siblings-tone-2 {
  --sibling-accent: #10b981;
}
.siblings-tone-3 {
  --sibling-accent: #f59e0b;
}

.siblings-card {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  margin: 0.35rem 0 0.45rem;
  padding: 0.85rem 0.95rem;
  border-radius: 12px;
  border: 1px solid #ececf1;
  border-left: 3px solid var(--sibling-accent, #7367f0);
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;
}

.siblings-tree-node:hover .siblings-card,
.siblings-tree-node:focus .siblings-card {
  border-color: #d8d0ff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.siblings-avatar-wrap {
  flex-shrink: 0;
}

.siblings-avatar {
  border: 1px solid #ececf1;
}

.siblings-card-info {
  min-width: 0;
  flex: 1;
}

.siblings-name-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
}

.siblings-name {
  font-weight: 700;
  color: #2c2c34;
  font-size: 0.96rem;
}

.siblings-status {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.5rem;
  border-radius: 5px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.siblings-status.is-present {
  color: #047857;
  background: #ecfdf5;
}
.siblings-status.is-left {
  color: #b91c1c;
  background: #fef2f2;
}
.siblings-status.is-registered {
  color: #0369a1;
  background: #f0f9ff;
}
.siblings-status.is-suspend {
  color: #b45309;
  background: #fffbeb;
}
.siblings-status.is-alumni,
.siblings-status.is-default {
  color: #4b5563;
  background: #f3f4f6;
}

.siblings-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.45rem;
}

.siblings-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid #efeff3;
  color: #6e6b7b;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
}

.siblings-contacts {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.4rem;
  color: #8f8f9d;
  font-size: 0.75rem;
  font-weight: 500;
}

.siblings-open-hint {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  margin-top: 0.35rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #a8a5b8;
  background: #f7f7fa;
  transition: color 0.15s ease, background 0.15s ease;
}

.siblings-tree-node:hover .siblings-open-hint {
  color: var(--sibling-accent, #7367f0);
  background: #f3f0ff;
}

@media (max-width: 767.98px) {
  .siblings-shell {
    padding: 1rem 0.85rem 1.05rem;
  }

  .siblings-card {
    gap: 0.65rem;
    padding: 0.8rem;
  }

  .siblings-open-hint {
    display: none;
  }
}
.history-card {
  position: relative;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #e0d8ff;
    box-shadow: 0 4px 16px rgba(124, 93, 250, 0.1);
  }
}

.history-card__body {
  flex: 1;
  min-width: 0;
  padding: 1rem 1.1rem;
}

.history-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0.8rem;
}

.history-card__badge {
  display: inline-flex !important;
  align-items: center;
  gap: 0.35rem;
  padding: 0.38rem 0.72rem !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  border-radius: 8px !important;
  letter-spacing: 0.01em;
}

.history-card__date {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.32rem 0.62rem;
  border-radius: 8px;
  border: 1px solid #eef0f4;
  background: #f9fafb;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.history-card__route {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 0.75rem;
}

.history-card__route-from,
.history-card__route-to {
  display: inline-block;
  padding: 0.42rem 0.78rem;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.35;
  word-break: break-word;
}

.history-card--transfer .history-card__route-from {
  background: #fef2f2;
  color: #991b1b;
  text-decoration: line-through;
  text-decoration-color: rgba(153, 27, 27, 0.35);
}

.history-card--transfer .history-card__route-to {
  background: #f0fdf4;
  color: #166534;
}

.history-card--class .history-card__route-from {
  background: #f3f4f6;
  color: #4b5563;
}

.history-card--class .history-card__route-to {
  background: #ecfeff;
  color: #0e7490;
}

.history-card--default .history-card__route-from,
.history-card--default .history-card__route-to {
  background: #f9fafb;
  color: #374151;
}

.history-card__route-arrow {
  color: #9ca3af;
  flex-shrink: 0;
}

.history-card__details {
  font-size: 0.92rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.5;
}

.history-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.7rem;
  margin-top: 0.15rem;
  border-top: 1px dashed #e8ecf1;
}

.history-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.38rem 0.68rem;
  border-radius: 8px;
  border: 1px solid #e8e4ff;
  background: #f8f6ff;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.history-change-row {
  margin-bottom: 0.65rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.history-field-label {
  font-size: 0.85rem;
  min-width: 100px;
}

.history-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 0.45rem 1rem;
  border-radius: 10px;
  line-height: 1.3;
}

.history-pill--prev {
  background: #fdf2f2;
  color: #991b1b;
  border: 1px solid #fde2e2;
}

.history-pill--muted {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.history-pill--next-transfer {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.history-pill--next-class {
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

.history-pill--next-section {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.history-pill--next-session {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fef3c7;
}

.history-pill--next-status {
  background: #fdf2f2;
  color: #991b1b;
  border: 1px solid #fde2e2;
}

.history-pill--next-promote {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.history-pill--next-primary {
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #ddd6fe;
}

.history-arrow-icon {
  color: #9ca3af;
}

.scrollable-tab-content {
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 15px;
}

.scrollable-tab-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-tab-content::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-tab-content::-webkit-scrollbar-thumb {
  background: #dbdbdb;
  border-radius: 6px;
}

.scrollable-tab-content::-webkit-scrollbar-thumb:hover {
  background: #b3b3b3;
}

.fee-dues-section {
  background: #ffffff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 1rem 1.15rem;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}

.fee-data-table {
  margin-top: 0.25rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;

  ::v-deep thead th {
    background: linear-gradient(180deg, #f8f6ff 0%, #f3f0ff 100%) !important;
    color: #5b21b6 !important;
    font-weight: 700 !important;
    font-size: 0.7rem !important;
    letter-spacing: 0.06em;
    border-bottom: 1px solid #e0d8ff !important;
    padding: 0.85rem 1rem !important;
  }

  ::v-deep tbody tr {
    transition: background-color 0.15s ease;

    &:hover {
      background: #faf9ff !important;
    }
  }

  ::v-deep tbody tr.b-table-row-selected {
    background: #f5f3ff !important;
  }

  ::v-deep tbody td {
    padding: 0.9rem 1rem !important;
    border-top: 1px solid #f3f4f6 !important;
    font-size: 0.9rem;
  }
}

.fee-period-badge {
  display: inline-block;
  padding: 0.25rem 0.65rem;
  border-radius: 8px;
  background: #f3f0ff;
  color: #6d28d9;
  font-size: 0.8rem;
  font-weight: 600;
}

.fee-hold-badge {
  display: inline-block;
}

.fee-balance-amount {
  display: inline-block;
  padding: 0.3rem 0.75rem;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e;
  font-weight: 700;
  font-size: 0.9rem;
}

.subject-view-table {
  .subject-view-text {
    color: #4b5563;
    font-weight: 500;
  }

  .subject-view-amount {
    color: #1f2937;
    font-weight: 600;
  }

  .subject-view-share {
    color: #6b7280;
    font-weight: 600;
  }
}

.dues-summary-bar {
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f6ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  gap: 0.75rem;
}

.dues-summary-bar__label {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
}

.dues-summary-bar__amount {
  font-size: 1.35rem;
  font-weight: 800;
  color: #5b21b6;
  letter-spacing: -0.02em;
}

.fee-history-section {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  padding-left: 0.35rem;
  padding-right: 0.15rem;
  border-top: 1px dashed #e5e7eb;
  overflow: visible;
}

.fee-history-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.75rem;
}

.fee-history-section__title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
  color: #2d1b69;
}

.print-challan-btn {
  border-color: #d4c7ff !important;
  color: #7c5dfa !important;
  font-weight: 600;
}

.modern-table {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eef0f4;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);

  ::v-deep table {
    margin-bottom: 0;
    background: #ffffff;
  }

  ::v-deep thead th {
    background: #f9fafb !important;
    color: #6b7280 !important;
    font-weight: 600 !important;
    text-transform: uppercase;
    font-size: 0.72rem !important;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #e5e7eb !important;
    border-top: none !important;
    padding: 0.75rem 1rem !important;
    vertical-align: middle;
    white-space: nowrap;
  }

  ::v-deep tbody td {
    vertical-align: middle;
    color: #374151;
    font-size: 0.9rem;
    border-top: 1px solid #f3f4f6 !important;
    padding: 0.8rem 1rem !important;
  }

  ::v-deep tbody tr {
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #fafafa !important;
    }
  }

  ::v-deep tbody tr.b-table-row-selected {
    background-color: #f5f3ff !important;
  }

  ::v-deep .table-empty {
    display: none;
  }
}

.table-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.75rem 1rem;
  color: #9ca3af;
  font-size: 0.9rem;
  font-weight: 500;
  background: #ffffff;

  svg {
    color: #d1d5db;
  }
}

.panel-footer {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid #eef0f4;
}

.panel-total {
  color: #4b5563;
  font-size: 0.9rem;

  strong {
    color: #1f2937;
    font-weight: 700;
    font-size: 1rem;
  }
}

::v-deep .user-plan-tabs .custom-nav-tabs {
  background: #f8f9fb;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 0.35rem;
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  gap: 0.35rem;
  border-bottom: none;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  .nav-item {
    flex: 0 0 auto;
    margin-bottom: 0;

    .nav-link {
      border: 1px solid transparent !important;
      color: #6b7280 !important;
      font-weight: 600;
      font-size: 0.84rem;
      padding: 0.5rem 0.95rem !important;
      margin-right: 0;
      border-radius: 10px !important;
      transition: all 0.2s ease;
      background: transparent !important;
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;

      &:hover {
        color: #7c5dfa !important;
        background: #ffffff !important;
      }

      &.active,
      &.custom-nav-active {
        color: #7c5dfa !important;
        background: #ffffff !important;
        border-color: #e0d8ff !important;
        font-weight: 700;
        box-shadow: 0 2px 10px rgba(124, 93, 250, 0.12) !important;
      }
    }
  }
}

.vuexy-timeline {
  position: relative;
  padding-left: 1.5rem;
  padding-right: 0.15rem;
  overflow: visible;
}

.vuexy-timeline .timeline-item {
  position: relative;
  padding-bottom: 2.5rem;
  padding-left: 2.5rem;
  border-left: 2px solid #ebe9f1;
}

.vuexy-timeline .timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}

.vuexy-timeline .timeline-point {
  position: absolute;
  left: 0;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #fff;
  border: 2px solid #7367f0;
  border-radius: 50%;
  color: #7367f0;
  box-shadow: 0 4px 8px rgba(115, 103, 240, 0.15);
  z-index: 2;
}

.vuexy-timeline .timeline-point.timeline-point--class {
  border-color: #00cfe8;
  color: #00cfe8;
  box-shadow: 0 4px 8px rgba(0, 207, 232, 0.15);
}

.vuexy-timeline .timeline-event {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease-in-out;
}

.vuexy-timeline .timeline-event:hover {
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.08);
  border-color: rgba(115, 103, 240, 0.3);
  transform: translateY(-2px);
}

.timeline-badge {
  padding: 0.4rem 0.6rem;
  font-size: 85%;
}

.letter-spacing {
  letter-spacing: 0.5px;
}

.empty-state-box {
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
}

.empty-state-icon {
  color: #cbb9ff !important;
}

.empty-state-title {
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  font-weight: 500;
  color: #6b7280;
}

.complaints-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.complaints-add-btn {
  flex: 1 1 auto;
  min-height: 42px;
  padding: 0.55rem 1rem;
  font-weight: 600;
  border-radius: 10px;
}

.complaints-filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
}

.complaints-filter-btn {
  flex: 1 1 auto;
  min-width: 7rem;
  min-height: 42px;
  padding: 0.55rem 1rem;
  font-weight: 600;
  border-radius: 10px;
}

.user-plan-panel {
  max-width: 100%;
  overflow-x: hidden;
}

@media (max-width: 991.98px) {
  .tab-panel-body,
  .content-panel {
    padding: 0.9rem !important;
  }

  .fee-dues-section {
    padding: 0.85rem;
  }

  .scrollable-tab-content:not(.transfer-history-shell) {
    max-height: none;
    padding-right: 0;
  }

  .tab-data-table ::v-deep .b-table-sticky-header {
    max-height: 50vh;
  }
}

@media (max-width: 767.98px) {
  .tab-label {
    font-size: 0.8rem;
  }

  .tab-toolbar {
    flex-direction: column;
    align-items: stretch !important;
    margin-bottom: 0.75rem;
  }

  .tab-toolbar > div {
    width: 100%;
  }

  .fee-toolbar-actions {
    justify-content: stretch !important;
    gap: 0.5rem;
  }

  .fee-toolbar-actions .btn,
  .tab-toolbar .btn-icon {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;
    margin-right: 0 !important;
  }

  .fee-toolbar-actions .print-challan-btn {
    flex: 1 1 100%;
  }

  .filter-btn-group {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    gap: 0.45rem !important;
  }

  .filter-period-btn {
    width: 100%;
    padding: 0.5rem 0.5rem !important;
    font-size: 0.78rem !important;
  }

  .filter-btn-group > .btn:not(.filter-period-btn) {
    grid-column: 1 / -1;
    width: 100%;
    margin-left: 0 !important;
  }

  .attendance-export-btn {
    width: 100%;
  }

  .complaints-toolbar {
    flex-direction: column;
  }

  .complaints-add-btn,
  .complaints-filter-group {
    width: 100%;
    margin-left: 0;
  }

  .complaints-filter-btn {
    flex: 1;
    min-width: 0;
  }

  .dues-summary-bar {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.35rem;
  }

  .dues-summary-bar__amount {
    font-size: 1.5rem;
  }

  .dues-summary-bar .btn {
    width: 100%;
    margin-left: 0 !important;
  }

  .fee-history-section__head {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .tab-data-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    ::v-deep thead th {
      padding: 0.55rem 0.6rem !important;
      font-size: 0.62rem !important;
    }

    ::v-deep tbody td {
      padding: 0.55rem 0.6rem !important;
      font-size: 0.8rem;
      white-space: normal;
    }
  }

  .table-text-cell {
    max-width: none;
  }

  .table-date-chip {
    font-size: 0.75rem;
    white-space: normal;
    text-align: center;
    line-height: 1.25;
  }

  .vuexy-timeline:not(.transfer-history-timeline) {
    padding-left: 0;
  }

  .vuexy-timeline:not(.transfer-history-timeline) .timeline-item {
    padding-left: 0;
    border-left: none;
    padding-bottom: 0.85rem;
  }

  .vuexy-timeline:not(.transfer-history-timeline) .timeline-point {
    display: none;
  }

  .vuexy-timeline:not(.transfer-history-timeline) .timeline-event {
    padding: 0.9rem 1rem;

    &:hover {
      transform: none;
    }
  }

  .transfer-history-timeline .timeline-item {
    padding-left: 2rem;
    padding-bottom: 1.25rem;
  }

  .transfer-history-timeline .timeline-point {
    width: 30px;
    height: 30px;
    top: 6px;
    display: flex;
  }

  .transfer-history-timeline.vuexy-timeline {
    padding-left: 1.35rem;
  }

  .transfer-history-timeline .timeline-event.history-card:hover {
    transform: none;
  }

  .history-card__body {
    padding: 0.85rem 0.9rem;
  }

  .history-card__route {
    flex-direction: column;
    align-items: flex-start;
  }

  .history-card__route-arrow {
    transform: rotate(90deg);
    margin: 0.15rem 0;
  }

  .history-card__top {
    flex-direction: column;
    align-items: flex-start;
  }

  .transfer-history-shell {
    padding: 0.15rem 0 !important;
  }
}

@media (max-width: 575.98px) {
  .tab-panel-body,
  .content-panel,
  .fee-dues-section {
    border-radius: 10px !important;
    padding: 0.75rem !important;
  }

  ::v-deep .user-plan-tabs .custom-nav-tabs .nav-item .nav-link {
    padding: 0.4rem 0.55rem !important;
    font-size: 0.72rem;
    gap: 0.2rem;
  }

  ::v-deep .user-plan-tabs .custom-nav-tabs .nav-item .nav-link svg {
    width: 15px;
    height: 15px;
  }

  .tab-data-table ::v-deep .b-table-sticky-header {
    max-height: 45vh;
  }

  .fee-period-badge,
  .fee-balance-amount {
    font-size: 0.75rem;
  }
}
</style>

<style lang="scss">
body.user-view-sidebar-open {
  overflow: hidden;

  .b-sidebar-outer {
    z-index: 12000 !important;
  }

  .b-sidebar-backdrop {
    z-index: 12000 !important;
  }

  .b-sidebar {
    z-index: 12001 !important;
    background: #ffffff !important;
    pointer-events: auto !important;
  }
}

.user-plan-panel.plan-sidebars-active {
  position: static;
}

.plan-form-sidebar {
  .plan-sidebar-header,
  .content-sidebar-header {
    padding: 1rem 1.25rem !important;
    border-bottom: 1px solid #eef0f4;
    background: linear-gradient(135deg, #f8f6ff 0%, #ffffff 100%);

    h5 {
      font-weight: 700;
      color: #2d1b69;
      font-size: 1.05rem;
    }
  }

  .plan-sidebar-form,
  .b-sidebar-body > form {
    padding: 1.25rem !important;
  }

  .plan-sidebar-btn,
  .btn-block {
    min-height: 44px;
    padding: 0.65rem 1rem;
    font-weight: 600;
    border-radius: 10px;
  }

  .form-group label {
    font-weight: 600;
    color: #4b5563;
    font-size: 0.85rem;
  }

  .form-control {
    min-height: 42px;
    border-radius: 10px;
    border-color: #e5e7eb;

    &:focus {
      border-color: #c4b5fd;
      box-shadow: 0 0 0 0.15rem rgba(124, 93, 250, 0.15);
    }
  }
}

@media (max-width: 767.98px) {
  .plan-form-sidebar,
  .plan-form-sidebar.b-sidebar {
    width: 100% !important;
    max-width: 100vw !important;
  }
}
</style>
